import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Conversation, Attachment, UserData, Message, HydratedPost, Post, PostType, MediaType, MediaAttachment, Community, Channel, SystemMod } from '../types';
import { useScrollLock } from '../hooks/useScrollLock';
import { useUpload } from '../contexts/UploadContext';
import { uploadMediaFile, compressVideo, compressAudio, compressImage, downloadMediaUrl } from '../utils/mediaUtils';
import { ForwardMessageModal, DeleteMessageModal, ConversationThemeModal } from './MessageModals';
import { ReplyMediaPreview, AudioDurationDisplay, CustomAudioPlayer, AudioRecorder, AudioRecorderHandle } from './AudioComponents';
import { MediaViewerModal } from './MediaViewerModal';
import {
    ChevronLeftIcon, UsersIcon,
    SettingsIcon, EditIcon,
    ExternalLinkIcon, GalleryIcon, ChevronRightIcon,
    ReplyIcon,
    VolumeHighIcon, PaperclipIcon, MicIcon, PlusIcon, SendIcon, MoreHorizontalIcon, TrashIcon, EmojiIcon, DownloadIcon,
    SearchIcon, MailIcon, CloseIcon, AudioIcon, CopyIcon, PasteIcon, ForwardIcon, HashtagIcon, CameraIcon, SparklesIcon, VideoIcon, PhoneIcon
} from './Icons';
import { EmojiPickerModal } from './EmojiPickerModal';
import { GifPickerModal } from './GifPickerModal';
import { LinkifyText } from './LinkifyText';
import { FormattedText } from './FormattedText';
import api, { editDirectMessageOnServer, inviteToYipYap, API_URL } from '../api';
import { useMentionSearch } from '../hooks/useMentionSearch';
import { MentionDropdown } from './MentionDropdown';
import { NativeCamera } from './NativeCamera';
import { MOD_REGISTRY, getDynamicModDefinition } from '../utils/ModRegistry';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { requestMicrophonePermission, VOICE_AUDIO_CONSTRAINTS } from '../utils/permissionUtils';
import { VisualModPlayground, PlaygroundElement, PlaygroundElementType } from './VisualModPlayground';
import ProxiedIframe from './ProxiedIframe';
import { stickerTextStyle } from './StickerText';
import { getSlideIntervalMs } from '../utils/slideshowUtils';
import { PostScreenshot } from './PostScreenshot';
import { TiktokEmbed } from './PostCard';
import { toPng } from 'html-to-image';
import html2canvas from 'html2canvas';


const hexToRgba = (hex: string, opacity: number) => {
    try {
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
            cleanHex = cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2];
        }
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return isNaN(r) || isNaN(g) || isNaN(b) ? null : `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } catch (e) {
        return null;
    }
};

const MAX_DRAG_DISTANCE = typeof window !== 'undefined' ? window.innerWidth * 0.1 : 50;
const DRAG_THRESHOLD = 30; // Minimum drag distance to trigger action
const DEFAULT_EMOJI_LIST = ['❤️', '👍', '😂', '😮', '😢', '😡'];

const getEmojiSizeClass = (text: string | null | undefined) => {
    if (!text) return '';
    const trimmed = text.trim();
    if (trimmed.length === 0) return '';

    const emojiRegex = /\p{Extended_Pictographic}\uFE0F?[\u{1F3FB}-\u{1F3FF}]?(?:\u200D\p{Extended_Pictographic}\uFE0F?[\u{1F3FB}-\u{1F3FF}]?)*/gu;
    const stripped = trimmed.replace(emojiRegex, '').replace(/\s/g, '');

    if (stripped.length > 0) return '';

    // If it's pure emojis, count them to determine size
    const matches = trimmed.match(emojiRegex);
    const count = matches ? matches.length : 0;

    if (count === 1) return 'text-7xl py-4 text-center w-full select-none';
    if (count === 2) return 'text-5xl py-3 text-center w-full select-none';
    if (count === 3) return 'text-4xl py-2 text-center w-full select-none';
    return 'text-2xl py-1 text-center w-full select-none';
};

const MessageLinkPreview = React.memo(({ preview }: { preview: NonNullable<Message['linkPreview']> }) => (
    <a href={preview.url || undefined} target="_blank" rel="noopener noreferrer"
        className="mt-2 block bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700/50 hover:border-gray-600 transition-colors w-[280px] max-w-full">
        {preview.image && <img src={preview.image} alt="" className="w-full h-24 object-cover" />}
        <div className="p-2">
            <h4 className="font-bold text-sm truncate">{preview.title}</h4>
            <p className="text-xs text-gray-300 line-clamp-2">{preview.description}</p>
            <p className="text-xs text-gray-500 mt-1">{preview.domain}</p>
        </div>
    </a>
));

export const DynamicSharedPostPreview = React.memo(({ post, authorData, fallbackPostId, onPostClick, bgOverrideUrl, postsCount }: { post?: any, authorData?: any, fallbackPostId?: string | number, onPostClick?: (post: any) => void, bgOverrideUrl?: string, postsCount?: number }) => {
    const targetId = post?.id || fallbackPostId;
    const [scaledHeight, setScaledHeight] = React.useState<number>(280);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const scale = 280 / 1080;

    const touchStartRef = React.useRef<{ time: number; x: number; y: number }>({ time: 0, x: 0, y: 0 });
    const isDragOrHoldRef = React.useRef<boolean>(false);

    React.useEffect(() => {
        if (!contentRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const h = entry.contentRect.height * scale;
                if (h > 50) {
                    setScaledHeight(h);
                }
            }
        });
        observer.observe(contentRef.current);

        const timer1 = setTimeout(() => {
            if (contentRef.current) {
                const h = contentRef.current.offsetHeight * scale;
                if (h > 50) setScaledHeight(h);
            }
        }, 300);

        const timer2 = setTimeout(() => {
            if (contentRef.current) {
                const h = contentRef.current.offsetHeight * scale;
                if (h > 50) setScaledHeight(h);
            }
        }, 1200);

        return () => {
            observer.disconnect();
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, [scale]);

    if (!targetId) return null;

    const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        touchStartRef.current = { time: Date.now(), x: clientX, y: clientY };
        isDragOrHoldRef.current = false;
    };

    const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!touchStartRef.current.time) return;
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const diffX = Math.abs(clientX - touchStartRef.current.x);
        const diffY = Math.abs(clientY - touchStartRef.current.y);
        if (diffX > 10 || diffY > 10) {
            isDragOrHoldRef.current = true;
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        const pressDuration = touchStartRef.current.time ? Date.now() - touchStartRef.current.time : 0;
        if (isDragOrHoldRef.current || pressDuration > 400) {
            e.stopPropagation();
            e.preventDefault();
            return;
        }
        if (onPostClick) {
            onPostClick(post);
        } else if (post?.id) {
            window.location.href = `/post/${post.id}`;
        }
    };

    if (!post) {
        return (
            <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-lg bg-black/20 border border-white/10 flex items-center justify-center p-4 mt-2 cursor-pointer transition-colors hover:bg-black/30" onClick={() => onPostClick ? onPostClick({ id: targetId }) : window.location.href = `/post/${targetId}`}>
                <p className="text-white/50 italic text-sm">Post not found</p>
            </div>
        );
    }

    return (
        <div
            className="dynamic-shared-post-card mt-2 w-[280px] max-w-full rounded-2xl overflow-hidden cursor-pointer shadow-xl transition-transform hover:scale-[1.02] bg-[#1a1a2e]"
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onClick={handleClick}
        >
            <div style={{ height: scaledHeight !== undefined ? `${scaledHeight}px` : 'auto', overflow: 'hidden' }}>
                <div ref={contentRef} style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: '1080px', pointerEvents: 'none' }}>
                    <PostScreenshot post={post} authorData={authorData} inline={true} bgOverrideUrl={bgOverrideUrl} postsCount={postsCount} />
                </div>
            </div>
        </div>
    );
});

const MessageComponent = React.memo(({
    msg, isOwn, otherUser, onReply, onForward, onDelete, onAddReaction, theme, hideBubbles, nicknames, onNavigateToProfile, messageFontSize, currentUserId, messagesById, isGrouped, onScrollToMessage, onDragStart, onDragMove, onDragEnd, dragOffset, usersData, onDoubleTap,
    isEditing, editText, onEditChange, onSaveEdit, onCancelEdit,
    yipYapMode, anonymousAliases, sharedPost, onMediaClick, onPostClick, posts = []
}: {
    msg: Message,
    isOwn: boolean,
    otherUser: UserData | undefined,
    onReply: (m: Message) => void,
    onForward: (m: Message) => void,
    onDelete: (m: Message) => void,
    onAddReaction: (mid: number, emoji: string) => void,
    theme?: Conversation['theme'],
    hideBubbles?: boolean,
    nicknames?: Record<string, string>,
    onNavigateToProfile: (userId: string) => void,
    messageFontSize: number,
    currentUserId: string | null,
    messagesById: Map<number, Message>,
    isGrouped?: boolean,
    onScrollToMessage?: (msgId: number) => void,
    onDragStart: (e: React.TouchEvent | React.MouseEvent, msgId: number) => void,
    onDragMove: (e: React.TouchEvent | React.MouseEvent, msgId: number) => void,
    onDragEnd: (msgId: number) => void,
    dragOffset: number,
    usersData: Record<string, UserData>,
    onDoubleTap?: (msgId: number) => void,
    isEditing?: boolean,
    editText?: string,
    onEditChange?: (val: string) => void,
    onSaveEdit?: () => void,
    onCancelEdit?: () => void,
    yipYapMode?: 'broadcast' | 'anonymous' | 'forum',
    anonymousAliases?: Record<string, { name: string, pfp: string }>,
    sharedPost?: HydratedPost,
    onMediaClick?: (msgId: number, mediaIndex: number) => void,
    onPostClick?: (post: any) => void,
    posts?: Post[]
}) => {
    const lastTouchTimeRef = useRef<number>(0);
    const isNote = msg.isNote;
    const emojiSizeClass = getEmojiSizeClass(msg.text);
    const isStandalone = !!emojiSizeClass;
    const originalMessage = msg.replyTo?.id ? (messagesById.get(msg.replyTo.id) || msg.replyTo) : undefined;
    const isAnonymous = yipYapMode === 'anonymous';
    const actualSenderId = msg.senderId || (msg as any).authorId || 'unknown';
    const alias = anonymousAliases?.[actualSenderId];
    const authorId = actualSenderId;
    const authorData = isAnonymous
        ? {
            displayName: alias?.name || `yipyapper#${authorId.substring(0, 4)}`,
            pfp: alias?.pfp || `https://api.dicebear.com/7.x/bottts/svg?seed=${authorId}&backgroundColor=b6e3f4`,
            appThemeColor: '#4b5563'
        }
        : (isOwn ? (usersData[currentUserId!] || { displayName: 'You', pfp: '', appThemeColor: '#2563eb' }) : (otherUser || { displayName: authorId, pfp: '', appThemeColor: '#4b5563' }));
    
    const isSharedPost = !!msg.sharedPostId;
    const isLinkRequest = Boolean(msg.linkRequestDetails?.isLinkRequest || msg.metadata?.type === 'link_request' || msg.text?.startsWith('📍 Link Request') || msg.text?.includes("Let's link up! 📍"));
    const profileMatch = useMemo(() => {
        if (!msg.text) return null;
        const isInternalProfile = msg.text.includes('/profile/') && (
            msg.text.includes('goodlyfe.net') ||
            msg.text.includes(window.location.host) ||
            (!msg.text.startsWith('http') || msg.text.startsWith('/') || msg.text.startsWith('.'))
        );
        if (!isInternalProfile) return null;
        
        const match = msg.text.match(/(?:https?:\/\/[^\s]+)?\/profile\/([a-zA-Z0-9_.-]+)/i);
        return match ? match[1] : null;
    }, [msg.text]);
    const isProfileShareMsg = !!profileMatch;

    const youtubeVideoId = useMemo(() => {
        const urlToParse = msg.text || msg.linkPreview?.url || '';
        const match = urlToParse.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
        return match ? match[1] : null;
    }, [msg.text, msg.linkPreview]);

    const isYoutubeOnly = useMemo(() => {
        if (!youtubeVideoId || !msg.text) return false;
        const textWithoutUrl = msg.text.replace(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)[a-zA-Z0-9_-]{11}[^\s]*/gi, '').trim();
        return textWithoutUrl === '' && (!msg.media || msg.media.length === 0);
    }, [youtubeVideoId, msg.text, msg.media]);

    const tiktokVideoId = useMemo(() => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const textUrls = msg.text?.match(urlRegex) || [];
        const previewUrl = msg.linkPreview?.url ? [msg.linkPreview.url] : [];
        const allUrls = [...textUrls, ...previewUrl];
        
        const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/.*\/video\/(\d+)/;
        
        for (const url of allUrls) {
            const match = url.match(tiktokRegex);
            if (match && match[1]) {
                return match[1];
            }
        }
        
        const embedHtml = msg.linkPreview?.embedHtml || '';
        const embedMatch = embedHtml.match(/data-video-id="(\d+)"/) || embedHtml.match(/videoId=(\d+)/);
        if (embedMatch && embedMatch[1]) {
            return embedMatch[1];
        }
        
        return null;
    }, [msg.text, msg.linkPreview]);

    const [resolvedTiktokVideoId, setResolvedTiktokVideoId] = useState<string | null>(null);

    useEffect(() => {
        if (tiktokVideoId) {
            setResolvedTiktokVideoId(tiktokVideoId);
            return;
        }

        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const textUrls = msg.text?.match(urlRegex) || [];
        const previewUrl = msg.linkPreview?.url ? [msg.linkPreview.url] : [];
        const allUrls = [...textUrls, ...previewUrl];

        const tiktokUrl = allUrls.find(url => url.includes('tiktok.com'));
        if (tiktokUrl) {
            const resolveTiktok = async () => {
                try {
                    const response = await api.post('metadata', { url: tiktokUrl });
                    if (response.data && response.data.url) {
                        const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/.*\/video\/(\d+)/;
                        const resolvedMatch = response.data.url.match(tiktokRegex);
                        if (resolvedMatch && resolvedMatch[1]) {
                            setResolvedTiktokVideoId(resolvedMatch[1]);
                        }
                    }
                } catch (e) {
                    console.error("Failed to resolve TikTok URL inside message:", e);
                }
            };
            resolveTiktok();
        } else {
            setResolvedTiktokVideoId(null);
        }
    }, [tiktokVideoId, msg.text, msg.linkPreview]);

    const isTiktokOnly = useMemo(() => {
        if (!resolvedTiktokVideoId || !msg.text) return false;
        const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/[^\s]*/gi;
        const textWithoutUrl = msg.text.replace(tiktokRegex, '').trim();
        return textWithoutUrl === '' && (!msg.media || msg.media.length === 0);
    }, [resolvedTiktokVideoId, msg.text, msg.media]);

    const isMediaOnly = (!msg.text && msg.media && msg.media.length > 0) || isSharedPost || isYoutubeOnly || isTiktokOnly || isProfileShareMsg || isLinkRequest;

    if (isNote) {
        return (
            <div className="flex justify-center my-4 px-4 w-full">
                <div className="max-w-md text-center py-2 px-6 ">
                    <p className="text-xs font-bold text-gray-300 italic tracking-wide uppercase">{msg.text}</p>
                </div>
            </div>
        );
    }

    const ensureSolidHex = (color: string) => {
        if (!color) return color;
        if (color.startsWith('#') && color.length > 7) return color.substring(0, 7);
        return color;
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        const now = Date.now();
        if (now - lastTouchTimeRef.current < 300) {
            onDoubleTap?.(msg.id);
            lastTouchTimeRef.current = 0;
            e.preventDefault(); // Prevent zoom/other default double tap behavior
        } else {
            lastTouchTimeRef.current = now;
        }
        onDragStart(e, msg.id);
    };

    const bubbleOpacityVal = theme?.bubbleOpacity !== undefined ? theme.bubbleOpacity / 100 : 1;
    const getBubbleColor = (colorHex: string) => {
        return bubbleOpacityVal < 1 ? (hexToRgba(colorHex, bubbleOpacityVal) || colorHex) : colorHex;
    };

    const bubbleStyle = {
        backgroundColor: (hideBubbles || isStandalone || isMediaOnly || isSharedPost || isProfileShareMsg || isLinkRequest)
            ? 'transparent'
            : (isOwn
                ? getBubbleColor(ensureSolidHex(theme?.sentBubbleColor || '#2563eb'))
                : getBubbleColor(ensureSolidHex(theme?.receivedBubbleColor || '#4b5563'))),
        color: theme?.textColor || 'white',
        borderRadius: (hideBubbles || isSharedPost || isProfileShareMsg || isLinkRequest) ? '0' : undefined,
        padding: (hideBubbles || isStandalone || isMediaOnly || isSharedPost || isProfileShareMsg || isLinkRequest) ? '0' : undefined,
        border: 'none',
        boxShadow: (isMediaOnly || isSharedPost || isProfileShareMsg || isLinkRequest || hideBubbles || isStandalone) ? 'none' : undefined,
        backgroundImage: hideBubbles ? 'none' : undefined
    };

    if (hideBubbles) {
        return (
            <div id={`message-${msg.id}`} className={`group relative w-full px-4 message-item ${isGrouped ? 'mt-0' : 'mt-4'}`}>
                <div className="flex gap-3 transition-transform duration-200" style={{ transform: `translateX(${dragOffset}px)` }}>
                    {dragOffset !== 0 && (
                        <div className={`absolute top-1/2 -translate-y-1/2 transition-opacity flex items-center ${dragOffset > 0 ? 'left-[-30px]' : 'right-[-30px]'
                            }`} style={{ opacity: Math.abs(dragOffset) / 50 }}>
                            {dragOffset < 0 ? (
                                isOwn ? <ReplyIcon className="w-5 h-5 text-blue-500" /> : null
                            ) : (
                                isOwn ? null : <ReplyIcon className="w-5 h-5 text-blue-500" />
                            )}
                        </div>
                    )}

                    <div className="flex-shrink-0 w-10">
                        {!isGrouped ? (
                            authorData.pfp ? (
                                <img src={authorData.pfp} alt="" className={`w-10 h-10 rounded-full object-cover ${isAnonymous ? 'cursor-default' : 'cursor-pointer'}`} onClick={() => !isAnonymous && onNavigateToProfile(authorId)} />
                            ) : (
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm transition-all ${isAnonymous ? 'cursor-default' : 'cursor-pointer hover:brightness-110'}`}
                                    style={{ backgroundColor: authorData.appThemeColor || '#2563eb' }}
                                    onClick={() => !isAnonymous && onNavigateToProfile(authorId)}
                                >
                                    {authorData.displayName[0]?.toUpperCase()}
                                </div>
                            )
                        ) : (
                            <span className="text-[10px] text-gray-600 group-hover:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity leading-tight flex items-center justify-end w-full pr-2 select-none">
                                {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                        )}
                    </div>

                    <div 
                        className="flex-1 min-w-0"
                        onMouseDown={(e) => onDragStart(e, msg.id)}
                        onMouseMove={(e) => onDragMove(e, msg.id)}
                        onMouseUp={() => onDragEnd(msg.id)}
                        onMouseLeave={() => onDragEnd(msg.id)}
                        onTouchStart={handleTouchStart}
                        onTouchMove={(e) => onDragMove(e, msg.id)}
                        onTouchEnd={() => onDragEnd(msg.id)}
                        onDoubleClick={() => onDoubleTap?.(msg.id)}
                    >
                        {!isGrouped && (
                            <div className="flex items-center gap-2 mb-0.5">
                                <span
                                    className={`text-sm text-raised font-bold text-white ${isAnonymous ? 'cursor-default' : 'hover:underline cursor-pointer'}`}
                                    style={{ color: authorData.appThemeColor, ...stickerTextStyle(0.18) }}
                                    onClick={() => !isAnonymous && onNavigateToProfile(authorId)}
                                >
                                    {nicknames?.[authorId] || authorData.displayName}
                                </span>
                                <span className="text-[10px] text-gray-500 font-medium">
                                    {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                            </div>
                        )}

                        {originalMessage && (
                            <div className="mb-1 flex items-center gap-2 opacity-60 hover:opacity-100 cursor-pointer transition-opacity max-w-[80%] group/reply"
                                onClick={() => onScrollToMessage?.(originalMessage.id)}>
                                <div className="w-8 h-4 border-l-2 border-t-2 border-white/20 rounded-tl-lg ml-4 mb-[-8px]" />
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <span className="text-[11px] font-bold text-gray-400 group-hover/reply:text-blue-400" style={stickerTextStyle(0.12)}>
                                        @{isAnonymous && anonymousAliases?.[originalMessage.senderId]
                                            ? anonymousAliases[originalMessage.senderId].name
                                            : (nicknames?.[originalMessage.senderId] || (currentUserId && originalMessage.senderId === currentUserId ? (usersData[currentUserId]?.displayName || 'You') : originalMessage.senderId))}
                                    </span>
                                    <p className="text-[11px] text-gray-500 truncate italic">
                                        {originalMessage.text}
                                    </p>
                                </div>
                            </div>
                        )}

                        {isEditing ? (
                            <div className="flex flex-col gap-2 w-[85vw] md:w-[60vw] max-w-full mt-1 mb-2">
                                <textarea
                                    autoFocus
                                    value={editText}
                                    onChange={(e) => onEditChange?.(e.target.value)}
                                    // Rest of the textarea remains the same...
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            onSaveEdit?.();
                                        } else if (e.key === 'Escape') {
                                            onCancelEdit?.();
                                        }
                                    }}
                                    className="w-full bg-white/5 text-white rounded-lg p-2 text-sm outline-none border border-white/10 focus:border-white/20 transition-all resize-none"
                                    rows={Math.max(1, (editText || '').split('\n').length)}
                                />
                                <div className="flex items-center justify-start gap-2">
                                    <button onClick={onSaveEdit} className="px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-white/10 text-white hover:bg-white/20 transition-all">Save Changes</button>
                                    <button onClick={onCancelEdit} className="px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider text-white/50 hover:bg-white/5 transition-all">Cancel</button>
                                </div>
                            </div>
                        ) : (
                            (!isYoutubeOnly && !isTiktokOnly && msg.text && !isProfileShareMsg && !isLinkRequest) && (
                                <div className="text-gray-300 text-raised break-words leading-snug whitespace-pre-wrap">
                                    <FormattedText
                                        text={msg.text || ''}
                                        onNavigateToProfile={onNavigateToProfile}
                                        onNavigateToHashtag={() => { }}
                                        fontSize={messageFontSize}
                                        stickerize={true}
                                    />
                                </div>
                            )
                        )}

                        {msg.media && msg.media.length > 0 && !isLinkRequest && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {msg.media.map((media, idx) => (
                                    <div key={idx} className="max-w-[16rem] sm:max-w-md rounded-xl overflow-hidden border border-white/10 bg-black/40 shadow-xl">
                                        {media.type === 'image' && (
                                            <div className="relative group/msg-media">
                                                <img src={media.url} className="max-w-full h-auto cursor-pointer hover:scale-[1.02] transition-transform duration-300" alt="" onClick={() => onMediaClick?.(msg.id, idx)} />
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadMediaUrl(media.url, media.file?.name || 'image-attachment');
                                                    }}
                                                    className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/msg-media:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Save media"
                                                >
                                                    <DownloadIcon className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                        {media.type === 'video' && (
                                            <div className="relative group/msg-video">
                                                <video src={`${media.url}#t=0.001`} controls className="max-w-full h-auto" playsInline preload="metadata" />
                                                <button
                                                    onClick={() => onMediaClick?.(msg.id, idx)}
                                                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/msg-video:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Enlarge Video"
                                                >
                                                    <ExternalLinkIcon className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadMediaUrl(media.url, media.file?.name || 'video-attachment');
                                                    }}
                                                    className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/msg-video:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Save media"
                                                >
                                                    <DownloadIcon className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                        {(media.type === 'audio' || media.type === 'voice') && (
                                            <div className="relative group/msg-audio">
                                                <div className="p-2 min-w-[200px]"><CustomAudioPlayer src={media.url} authorId={msg.senderId || (msg as any).authorId || 'unknown'} /></div>
                                                <button
                                                    onClick={() => onMediaClick?.(msg.id, idx)}
                                                    className="absolute top-1 right-1 p-1 rounded-full bg-black/40 hover:bg-black/60 text-white/70 hover:text-white opacity-0 group-hover/msg-audio:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Enlarge Audio"
                                                >
                                                    <ExternalLinkIcon className="w-3 h-3" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadMediaUrl(media.url, media.file?.name || `${media.type}-attachment`);
                                                    }}
                                                    className="absolute bottom-1 right-1 p-1 rounded-full bg-black/40 hover:bg-black/60 text-white/70 hover:text-white opacity-0 group-hover/msg-audio:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Save media"
                                                >
                                                    <DownloadIcon className="w-3 h-3" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {(!isSharedPost && resolvedTiktokVideoId) ? (
                            <div className="mt-2 w-[280px] h-[497px] max-w-full rounded-2xl overflow-hidden shadow-lg border border-white/10 relative flex items-center justify-center bg-black">
                                <TiktokEmbed videoId={resolvedTiktokVideoId} apiUrl={API_URL} />
                            </div>
                        ) : (!isSharedPost && youtubeVideoId) ? (
                            <div className="mt-2 w-[280px] max-w-full rounded-2xl overflow-hidden shadow-lg border border-white/10 aspect-video">
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src={`${API_URL}youtube-proxy?videoId=${youtubeVideoId}&origin=${encodeURIComponent(window.location.origin)}`}
                                    title="YouTube video player"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    className="w-full h-full"
                                />
                            </div>
                        ) : (
                            (!isSharedPost && msg.linkPreview && !isProfileShareMsg) && <MessageLinkPreview preview={msg.linkPreview} />
                        )}

                        {isSharedPost && !isLinkRequest && (
                            <DynamicSharedPostPreview
                                post={sharedPost}
                                fallbackPostId={msg.sharedPostId}
                                authorData={sharedPost ? usersData[sharedPost.author] : undefined}
                                onPostClick={onPostClick}
                                bgOverrideUrl={msg.sharedPostBgUrl}
                            />
                        )}

                        {isLinkRequest && (() => {
                            const mediaUrl = msg.linkRequestDetails?.imageUrl || msg.metadata?.imageUrl;
                            const isVideo = Boolean(mediaUrl && (
                                mediaUrl.match(/\.(mp4|webm|ogg|mov|m4v|3gp)($|\?)/i) ||
                                mediaUrl.includes('/video/') ||
                                mediaUrl.includes('video') ||
                                mediaUrl.startsWith('blob:')
                            ));
                            const mediaType = isVideo ? 'video' : 'image';
                            const linkText = msg.linkRequestDetails?.text || msg.metadata?.text || (msg.text && msg.text !== '📍 Link Request' ? msg.text : undefined) || "Let's link up! 📍";
                            const linkReqVirtualPost = {
                                id: msg.id,
                                author: msg.senderId,
                                type: mediaUrl ? mediaType : 'text',
                                content: {
                                    text: linkText,
                                    media: mediaUrl ? [{ type: mediaType, url: mediaUrl }] : []
                                },
                                visibility: 'private',
                                createdAt: msg.timestamp,
                                likes: 0,
                                comments: msg.linkRequestDetails?.comments || msg.metadata?.comments || [],
                                vouches: [],
                                bumps: 0,
                                bookmarks: 0,
                                shares: 0,
                                linkRequestDetails: {
                                    isLinkRequest: true,
                                    targetUsers: [msg.senderId],
                                    location: (msg.linkRequestDetails?.location || (msg.metadata?.lat && msg.metadata?.lng ? { lat: msg.metadata.lat, lng: msg.metadata.lng } : undefined)),
                                    eventDate: msg.linkRequestDetails?.eventDate || msg.metadata?.eventDate,
                                    eventTime: msg.linkRequestDetails?.eventTime || msg.metadata?.eventTime,
                                    responses: msg.linkRequestDetails?.responses || msg.metadata?.responses || {}
                                }
                            };
                            const senderKey = Object.keys(usersData).find(k => k.toLowerCase() === (msg.senderId || '').replace(/^@/, '').toLowerCase()) || msg.senderId;
                            return (
                                <DynamicSharedPostPreview
                                    post={linkReqVirtualPost as any}
                                    fallbackPostId={msg.id}
                                    authorData={usersData[senderKey] || usersData[msg.senderId]}
                                    onPostClick={(p) => onPostClick && onPostClick(p || linkReqVirtualPost as any)}
                                    bgOverrideUrl={mediaUrl ? mediaUrl : (msg.metadata?.senderBgUrl || msg.sharedPostBgUrl)}
                                />
                            );
                        })()}


                        {isProfileShareMsg && profileMatch && (
                            <DynamicSharedPostPreview
                                post={{
                                    id: msg.id,
                                    author: profileMatch,
                                    type: 'profile',
                                    content: { text: '', username: profileMatch },
                                    timestamp: msg.timestamp,
                                    likes: 0,
                                    comments: [],
                                    visibility: 'public'
                                }}
                                fallbackPostId={msg.id}
                                authorData={usersData[profileMatch]}
                                onPostClick={() => onNavigateToProfile(profileMatch)}
                                bgOverrideUrl={msg.sharedPostBgUrl}
                                postsCount={posts.filter((p: any) => p.author === profileMatch).length}
                            />
                        )}

                        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                                {Object.entries(msg.reactions).map(([emoji, users]) => (
                                    <button key={emoji} onClick={() => onAddReaction(msg.id, emoji)}
                                        className={`flex items-center gap-1.5 px-2 py-1 rounded-xl text-sm border transition-all ${currentUserId && users.includes(currentUserId) ? 'bg-blue-500/20 border-blue-500/40 text-blue-300' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'}`}>
                                        <span className="sticker-outline">{emoji}</span>
                                        {users.length > 1 && <span className="font-bold text-[10px]">{users.length}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id={`message-${msg.id}`} className={`flex flex-col message-item ${isOwn ? 'items-end' : 'items-start'} group transition-all duration-200 ${isGrouped ? 'mt-0.5' : 'mt-2'}`}>
            {dragOffset !== 0 && (
                <div className={`absolute top-1/2 -translate-y-1/2 transition-opacity flex items-center ${dragOffset > 0 ? 'left-[-30px]' : 'right-[-30px]'
                    }`} style={{ opacity: Math.abs(dragOffset) / 50 }}>
                    {dragOffset < 0 ? (
                        isOwn ? <ReplyIcon className="w-5 h-5 text-blue-500" /> : null
                    ) : (
                        isOwn ? null : <ReplyIcon className="w-5 h-5 text-blue-500" />
                    )}
                </div>
            )}
            <div className={`flex items-end gap-2 max-w-[85%] ${isOwn ? 'flex-row-reverse' : 'flex-row'} transition-transform duration-200`} style={{ transform: `translateX(${dragOffset}px)` }}>
                <div className="flex flex-col gap-1">
                    {originalMessage && (
                        <div className={`text-[10px] text-white/40 mb-1 flex items-center gap-1 cursor-pointer hover:text-white/70 transition-colors ${isOwn ? 'justify-end' : 'justify-start'}`}
                            onClick={() => onScrollToMessage?.(originalMessage.id)}>
                            <ReplyIcon className="w-3 h-3" />
                            <span className="truncate max-w-[150px]" style={stickerTextStyle(0.12)}>
                                Replying to {isAnonymous && anonymousAliases?.[originalMessage.senderId]
                                    ? anonymousAliases[originalMessage.senderId].name
                                    : (nicknames?.[originalMessage.senderId] || (currentUserId && originalMessage.senderId === currentUserId ? (usersData[currentUserId]?.displayName || 'You') : otherUser?.displayName || originalMessage.senderId))}
                            </span>
                        </div>
                    )}

                    <div
                        className={`relative px-4 py-2.5 rounded-2xl shadow-lg transition-transform hover:scale-[1.01] message-bubble ${hideBubbles || isStandalone || isMediaOnly || isSharedPost || isProfileShareMsg ? '' : isOwn ? 'rounded-tr-none' : 'rounded-tl-none border border-white/5'
                            }`}
                        style={bubbleStyle}
                        onMouseDown={(e) => onDragStart(e, msg.id)}
                        onMouseMove={(e) => onDragMove(e, msg.id)}
                        onMouseUp={() => onDragEnd(msg.id)}
                        onMouseLeave={() => onDragEnd(msg.id)}
                        onTouchStart={handleTouchStart}
                        onTouchMove={(e) => onDragMove(e, msg.id)}
                        onTouchEnd={() => onDragEnd(msg.id)}
                        onDoubleClick={() => onDoubleTap?.(msg.id)}
                    >
                        {isEditing ? (
                            <div className="flex flex-col gap-2 w-[85vw] md:w-[60vw] max-w-full mt-1 mb-2">
                                <textarea
                                    autoFocus
                                    value={editText}
                                    onChange={(e) => onEditChange?.(e.target.value)}
                                    // Rest of the textarea remains the same...
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            onSaveEdit?.();
                                        } else if (e.key === 'Escape') {
                                            onCancelEdit?.();
                                        }
                                    }}
                                    className="w-full bg-black/20 text-white rounded-lg p-2 text-sm outline-none border border-white/10 focus:border-white/20 transition-all resize-none"
                                    rows={Math.max(1, (editText || '').split('\n').length)}
                                />
                                <div className="flex items-center justify-end gap-2">
                                    <button onClick={onCancelEdit} className="px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider text-white/50 hover:bg-white/5 transition-all">Cancel</button>
                                    <button onClick={onSaveEdit} className="px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-white/10 text-white hover:bg-white/20 transition-all">Save Changes</button>
                                </div>
                            </div>
                        ) : (
                            (!isSharedPost && msg.text && !isYoutubeOnly && !isTiktokOnly && !isProfileShareMsg && !isLinkRequest) && (
                                <div className={`whitespace-pre-wrap break-words leading-relaxed text-raised ${isStandalone ? emojiSizeClass : ''}`} style={{ fontSize: isStandalone ? undefined : `${messageFontSize}px` }}>
                                    <FormattedText
                                        text={msg.text}
                                        onNavigateToProfile={onNavigateToProfile}
                                        onNavigateToHashtag={() => { }}
                                        fontSize={isStandalone ? undefined : messageFontSize}
                                        stickerize={true}
                                    />
                                </div>
                            )
                        )}

                        {(!isSharedPost && msg.media && msg.media.length > 0) && (
                            <div className="mt-2 flex flex-col gap-2">
                                {msg.media.map((media, idx) => (
                                    <div key={idx} className="rounded-xl overflow-hidden relative group/msg-media">
                                        {media.type === 'image' && (
                                            <div className="relative group/msg-media">
                                                <img src={media.url} className="max-w-full max-h-80 object-contain cursor-pointer" alt="" onClick={() => onMediaClick?.(msg.id, idx)} />
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadMediaUrl(media.url, media.file?.name || 'image-attachment');
                                                    }}
                                                    className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/msg-media:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Save media"
                                                >
                                                    <DownloadIcon className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                        {media.type === 'video' && (
                                            <div className="relative group/msg-video">
                                                <video key={media.url} src={`${media.url}#t=0.001`} controls playsInline preload="metadata" className="max-w-full max-h-80" />
                                                <button
                                                    onClick={() => onMediaClick?.(msg.id, idx)}
                                                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/msg-video:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Enlarge Video"
                                                >
                                                    <ExternalLinkIcon className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadMediaUrl(media.url, media.file?.name || 'video-attachment');
                                                    }}
                                                    className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/msg-video:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Save media"
                                                >
                                                    <DownloadIcon className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                        {media.isLive && (
                                            <div className="absolute top-2 right-2 pointer-events-none opacity-50 transition-opacity group-hover/msg-media:opacity-80">
                                                <img src="/assets/goodieboy.png" alt="Live" className="w-6 h-6" />
                                            </div>
                                        )}
                                        {media.type === 'file' && (
                                            <a href={media.url} download={media.file?.name || 'document'} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 bg-black/20 hover:bg-black/40 border border-white/10 rounded-xl transition-colors w-full cursor-pointer max-w-sm mt-1 mb-1">
                                                <div className="p-2 bg-white/10 rounded-lg shrink-0">
                                                    <PaperclipIcon className="w-5 h-5 text-blue-400" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-bold text-white truncate" style={stickerTextStyle(0.18)}>{media.file?.name || 'Document'}</p>
                                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-0.5" style={stickerTextStyle(0.15)}>Click to download</p>
                                                </div>
                                            </a>
                                        )}
                                        {(media.type === 'audio' || media.type === 'voice') && (
                                            <div className="relative group/msg-audio">
                                                <div className="p-2 min-w-[200px]"><CustomAudioPlayer src={media.url} authorId={msg.senderId || (msg as any).authorId || 'unknown'} /></div>
                                                <button
                                                    onClick={() => onMediaClick?.(msg.id, idx)}
                                                    className="absolute top-1 right-1 p-1 rounded-full bg-black/40 hover:bg-black/60 text-white/70 hover:text-white opacity-0 group-hover/msg-audio:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Enlarge Audio"
                                                >
                                                    <ExternalLinkIcon className="w-3 h-3" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadMediaUrl(media.url, media.file?.name || `${media.type}-attachment`);
                                                    }}
                                                    className="absolute bottom-1 right-1 p-1 rounded-full bg-black/40 hover:bg-black/60 text-white/70 hover:text-white opacity-0 group-hover/msg-audio:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Save media"
                                                >
                                                    <DownloadIcon className="w-3 h-3" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {(!isSharedPost && resolvedTiktokVideoId) ? (
                            <div className="mt-2 w-[280px] h-[497px] max-w-full rounded-2xl overflow-hidden shadow-lg border border-white/10 relative flex items-center justify-center bg-black">
                                <TiktokEmbed videoId={resolvedTiktokVideoId} apiUrl={API_URL} />
                            </div>
                        ) : (!isSharedPost && youtubeVideoId) ? (
                            <div className="mt-2 w-[280px] max-w-full rounded-2xl overflow-hidden shadow-lg border border-white/10 aspect-video">
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src={`${API_URL}youtube-proxy?videoId=${youtubeVideoId}&origin=${encodeURIComponent(window.location.origin)}`}
                                    title="YouTube video player"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    className="w-full h-full"
                                />
                            </div>
                        ) : (
                            (!isSharedPost && msg.linkPreview && !isProfileShareMsg) && <MessageLinkPreview preview={msg.linkPreview} />
                        )}

                        {isSharedPost && !isLinkRequest && (
                            <DynamicSharedPostPreview
                                post={sharedPost}
                                fallbackPostId={msg.sharedPostId}
                                authorData={sharedPost ? usersData[sharedPost.author] : undefined}
                                onPostClick={onPostClick}
                                bgOverrideUrl={msg.sharedPostBgUrl}
                            />
                        )}

                        {isLinkRequest && (() => {
                            const mediaUrl = msg.linkRequestDetails?.imageUrl || msg.metadata?.imageUrl;
                            const isVideo = Boolean(mediaUrl && (
                                mediaUrl.match(/\.(mp4|webm|ogg|mov|m4v|3gp)($|\?)/i) ||
                                mediaUrl.includes('/video/') ||
                                mediaUrl.includes('video') ||
                                mediaUrl.startsWith('blob:')
                            ));
                            const mediaType = isVideo ? 'video' : 'image';
                            const linkText = msg.linkRequestDetails?.text || msg.metadata?.text || (msg.text && msg.text !== '📍 Link Request' ? msg.text : undefined) || "Let's link up! 📍";
                            const linkReqVirtualPost = {
                                id: msg.id,
                                author: msg.senderId,
                                type: mediaUrl ? mediaType : 'text',
                                content: {
                                    text: linkText,
                                    media: mediaUrl ? [{ type: mediaType, url: mediaUrl }] : []
                                },
                                visibility: 'private',
                                createdAt: msg.timestamp,
                                likes: 0,
                                comments: msg.linkRequestDetails?.comments || msg.metadata?.comments || [],
                                vouches: [],
                                bumps: 0,
                                bookmarks: 0,
                                shares: 0,
                                linkRequestDetails: {
                                    isLinkRequest: true,
                                    targetUsers: [msg.senderId],
                                    location: (msg.linkRequestDetails?.location || (msg.metadata?.lat && msg.metadata?.lng ? { lat: msg.metadata.lat, lng: msg.metadata.lng } : undefined)),
                                    eventDate: msg.linkRequestDetails?.eventDate || msg.metadata?.eventDate,
                                    eventTime: msg.linkRequestDetails?.eventTime || msg.metadata?.eventTime,
                                    responses: msg.linkRequestDetails?.responses || msg.metadata?.responses || {}
                                }
                            };
                            const senderKey = Object.keys(usersData).find(k => k.toLowerCase() === (msg.senderId || '').replace(/^@/, '').toLowerCase()) || msg.senderId;
                            return (
                                <DynamicSharedPostPreview
                                    post={linkReqVirtualPost as any}
                                    fallbackPostId={msg.id}
                                    authorData={usersData[senderKey] || usersData[msg.senderId]}
                                    onPostClick={(p) => onPostClick && onPostClick(p || linkReqVirtualPost as any)}
                                    bgOverrideUrl={mediaUrl ? mediaUrl : (msg.metadata?.senderBgUrl || msg.sharedPostBgUrl)}
                                />
                            );
                        })()}

                        {isProfileShareMsg && profileMatch && (
                            <DynamicSharedPostPreview
                                post={{
                                    id: msg.id,
                                    author: profileMatch,
                                    type: 'profile',
                                    content: { text: '', username: profileMatch },
                                    timestamp: msg.timestamp,
                                    likes: 0,
                                    comments: [],
                                    visibility: 'public'
                                }}
                                fallbackPostId={msg.id}
                                authorData={usersData[profileMatch]}
                                onPostClick={() => onNavigateToProfile(profileMatch)}
                                bgOverrideUrl={msg.sharedPostBgUrl}
                                postsCount={posts.filter((p: any) => p.author === profileMatch).length}
                            />
                        )}

                        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                            <div className={`absolute -bottom-2 ${isOwn ? 'right-0' : 'left-0'} flex -space-x-1 translate-y-1/2 z-10`}>
                                {Object.entries(msg.reactions).map(([emoji, users]) => (
                                    <button key={emoji} onClick={() => onAddReaction(msg.id, emoji)}
                                        className={`bg-[#1b1b1f] border border-white/10 rounded-full px-2 py-1 shadow-lg flex items-center gap-1 hover:scale-110 active:scale-95 transition-all ${currentUserId && users.includes(currentUserId) ? 'ring-1 ring-blue-500/50' : ''}`}>
                                        <span className="text-sm sticker-outline">{emoji}</span>
                                        {users.length > 1 && <span className="text-white/60 font-bold text-[10px]">{users.length}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});




interface MessagesPageProps {
    socket: any;
    conversations: Conversation[];
    currentUserId: string;
    usersData: Record<string, UserData>;
    onSendMessage: (conversationId: number, text: string, replyToId?: number, attachments?: Attachment[], isNote?: boolean, fontId?: string) => void;
    onDeleteConversation?: (conversationId: number) => void;
    onOpenCreateYipYapWithUser?: (userId: string) => void;
    onOpenCreateCommunityWithUser?: (userId: string) => void;
    onOpenNewMessage?: () => void;
    onOpenCreateYipYap?: () => void;
    onOpenCreateCommunity?: () => void;
    onNavigateToCommunity?: (id: string, channelId?: string) => void;
    activeConversationId: number | null;
    onSelectConversation: (conversationId: number) => void;
    onNavigateToProfile: (userId: string) => void;
    onAddReaction: (conversationId: number, messageId: number, emoji: string) => void;
    onDeleteMessage: (conversationId: number, messageId: number) => void;
    onForwardMessage: (message: Message, targetConversationIds: number[]) => void;
    onUpdateConversation: (conversationId: number, updates: Partial<Conversation>) => void;
    postBlurOpacity?: number;
    onClearConversation?: () => void;
    posts?: HydratedPost[];
    onPostClick?: (post: HydratedPost) => void;
    isDemo?: boolean;
    messageFontSize: number;
    updateMessageFontSize: (size: number) => void;
    onSwipeTransition?: (direction: 'left' | 'right') => void;
    communities?: Community[];
    onSelectCommunityChannel?: (communityId: string, channelId: string) => void;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    closeInternalModalsTrigger?: number;
    activeUsers?: string[];
    initialSharedContent?: { text: string, media: MediaAttachment[], sourcePostId?: number } | null;
    clearSharedContent?: () => void;
    customMods?: SystemMod[];
    activeSystemModId?: string;
    onOpenSystemModModal?: () => void;
    onPublishMod?: (modId: string, previewData: any) => void;

    // Mod Rendering Props
    appThemeColor?: string;
    onNavigate?: (page: any) => void;
    updateUserData?: any;

    animatingLikes?: Set<number>;
    animatingBumps?: Set<number>;
    animatingBookmarks?: Set<number>;
    galleryAnimation?: string;
    gridColumns?: number;
    onGridColumnsChange?: (cols: number) => void;
    onLike?: (postId: number) => void;
    onBump?: (post: any) => void;
    onBookmark?: (postId: number) => void;
    onEdit?: (post: any) => void;
    onDelete?: (postId: number) => void;
    onVouch?: (postId: number) => void;
    onReportPost?: (postId: number) => void;
    onNavigateToHashtag?: (tag: string) => void;
    onShare?: (post: any) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    dailyAchievements?: any;
    onCompleteAchievement?: (achievement: any, postData: any) => void;
    onOpenAdminSettings?: () => void;
    isAdmin?: boolean;
    voiceRooms?: Record<string, any[]>;
    onJoinVoice?: (communityId: string, channelId: string) => void;
    onLeaveVoice: () => void;
    activeVoiceData?: { communityId: string, channelId: string, users?: any[] } | null;
    requestedTab?: 'dm' | 'yipyap' | 'community';
}


export const MessagesPage = ({
    socket,
    conversations,
    currentUserId,
    usersData,
    onSendMessage,
    onSelectConversation,
    activeConversationId,
    onNavigateToProfile,
    onAddReaction,
    onDeleteMessage,
    onForwardMessage,
    onUpdateConversation,
    postBlurOpacity = 0,
    onClearConversation,
    posts = [],
    onPostClick,
    isDemo = false,
    messageFontSize,
    updateMessageFontSize,
    onSwipeTransition,
    communities = [],
    onSelectCommunityChannel,
    onInternalModalStateChange,
    closeInternalModalsTrigger = 0,
    activeUsers = [],
    initialSharedContent,
    clearSharedContent,
    onDeleteConversation,
    onOpenCreateYipYapWithUser,
    onOpenCreateCommunityWithUser,
    onOpenNewMessage,
    onOpenCreateYipYap,
    onOpenCreateCommunity,
    onNavigateToCommunity,
    customMods = [],
    activeSystemModId,
    onOpenSystemModModal,
    onPublishMod,
    appThemeColor,
    onNavigate,
    updateUserData,

    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    gridColumns,
    onGridColumnsChange,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onVouch,
    onReportPost,
    onNavigateToHashtag,
    onShare,
    onMuteUser,
    onBlockUser,
    onGrantAdmin,
    onDeleteUser,
    dailyAchievements,
    onCompleteAchievement,
    onOpenAdminSettings,
    isAdmin,
    voiceRooms,
    onJoinVoice,
    onLeaveVoice,
    activeVoiceData,
    requestedTab
}: MessagesPageProps) => {
    const [activeTab, setActiveTab] = useState<'dm' | 'yipyap' | 'community'>('dm');
    const [bgSlideshowIdx, setBgSlideshowIdx] = useState(0);
    const [showMobilePreview, setShowMobilePreview] = useState(false);
    const [playgroundElements, setPlaygroundElements] = useState<PlaygroundElement[]>([]);
    const [modPreviewData, setModPreviewData] = useState<any>(null);
    const initialPinchDistanceRef = useRef<number | null>(null);
    const initialFontSizeRef = useRef<number>(14);
    const [fetchedSharedPosts, setFetchedSharedPosts] = useState<Record<number, any>>({});
    const [activeMediaViewerIndex, setActiveMediaViewerIndex] = useState<number | null>(null);
    
    useEffect(() => {
        if (requestedTab) {
            setActiveTab(requestedTab);
        }
    }, [requestedTab]);

    useEffect(() => {
        if (!activeConversationId || isDemo) return;
        const convo = conversations.find(c => c.id === activeConversationId);
        if (!convo || !convo.messages) return;

        const missingIds = new Set<number>();
        convo.messages.forEach(msg => {
            if (msg.sharedPostId && !posts.find(p => p.id === msg.sharedPostId) && !fetchedSharedPosts[msg.sharedPostId]) {
                missingIds.add(msg.sharedPostId);
            }
        });

        if (missingIds.size > 0) {
            import('../api').then(({ default: api }) => {
                missingIds.forEach(id => {
                    api.get(`/posts/${id}`).then(res => {
                        if (res.data) {
                            setFetchedSharedPosts(prev => ({ ...prev, [id]: res.data }));
                        }
                    }).catch(err => console.error('Failed to fetch missing shared post:', id, err));
                });
            });
        }
    }, [activeConversationId, conversations, posts, fetchedSharedPosts, isDemo]);



    const activeConversation = conversations.find(c => c.id === activeConversationId);

    // Reset background index on conversation change
    useEffect(() => {
        setBgSlideshowIdx(0);
    }, [activeConversationId]);

    // Background slideshow interval timer
    useEffect(() => {
        const slideshow = activeConversation?.theme?.backgroundSlideshow;
        let timer: any = null;
        if (activeConversation?.theme?.backgroundMode === 'image' && slideshow && slideshow.attachments && slideshow.attachments.length > 1) {
            let intervalMs = (slideshow.intervalValue || 10) * 1000;
            if (slideshow.intervalUnit === 'minutes') intervalMs *= 60;
            else if (slideshow.intervalUnit === 'hours') intervalMs *= 3600;
            else if (slideshow.intervalUnit === 'days') intervalMs *= 86400;

            timer = setInterval(() => {
                setBgSlideshowIdx(prev => (prev + 1) % slideshow.attachments.length);
            }, intervalMs);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [activeConversation?.theme?.backgroundSlideshow, activeConversation?.theme?.backgroundMode]);
    const conversationMedia = useMemo(() => {
        if (!activeConversation || !activeConversation.messages) return [];
        const mediaList: any[] = [];
        activeConversation.messages.forEach((msg) => {
            if (msg.hiddenBy?.includes(currentUserId || '')) return;
            if (msg.media && msg.media.length > 0) {
                msg.media.forEach((attachment, idx) => {
                    mediaList.push({
                        url: attachment.url,
                        type: attachment.type,
                        messageId: msg.id,
                        mediaIndex: idx,
                        senderId: msg.senderId || (msg as any).authorId || 'unknown',
                        timestamp: msg.timestamp,
                        caption: msg.text
                    });
                });
            }
        });
        return mediaList;
    }, [activeConversation, currentUserId]);

    const handleMediaClick = useCallback((msgId: number, mediaIndex: number) => {
        const index = conversationMedia.findIndex(
            m => m.messageId === msgId && m.mediaIndex === mediaIndex
        );
        if (index !== -1) {
            setActiveMediaViewerIndex(index);
        }
    }, [conversationMedia]);

    const isAnonymous = activeConversation?.yipYapMode === 'anonymous';
    const [displayLimit, setDisplayLimit] = useState(30);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteSearchTerm, setInviteSearchTerm] = useState('');

    const [conversationMenuId, setConversationMenuId] = useState<number | null>(null);
    const [deletingConversationId, setDeletingConversationId] = useState<number | null>(null);
    const pressTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleInviteFollower = async (targetUser: string) => {
        try {
            const updatedConv = await inviteToYipYap(activeConversationId!, currentUserId, targetUser);
            onUpdateConversation(activeConversationId!, updatedConv);
            setShowInviteModal(false);
            setInviteSearchTerm('');
        } catch (error) {
            console.error('Failed to invite follower:', error);
            alert('Failed to invite follower.');
        }
    };

    useEffect(() => {
        setDisplayLimit(30);
        if (activeConversation) {
            if (activeConversation.isYipYap) {
                setActiveTab('yipyap');
            } else {
                setActiveTab('dm');
            }

            // Check for mod preview data in system messages
            const isSystemChat = activeConversation.participants.includes('goodlyfe');
            if (isSystemChat && activeSystemModId) {
                const lastSystemMsg = [...activeConversation.messages].reverse().find(m => m.senderId === 'goodlyfe');
                if (lastSystemMsg?.text) {
                    const match = lastSystemMsg.text.match(/\[PREVIEW_UI\]([\s\S]*?)\[\/PREVIEW_UI\]/);
                    if (match) {
                        try {
                            // In a real app, this might be JSON or code. 
                            // For now, let's just store the content.
                            setModPreviewData(match[1].trim());
                        } catch (e) {
                            console.error("Failed to parse preview UI", e);
                        }
                    }
                }
            }
        }
    }, [activeConversationId, activeConversation?.messages.length, activeSystemModId]);

    const [replyingTo, setReplyingTo] = useState<Message | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [activeMenu, setActiveMenu] = useState<number | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [showEmojiPickerFor, setShowEmojiPickerFor] = useState<number | 'input' | null>(null);
    const [forwardingMessage, setForwardingMessage] = useState<Message | null>(null);
    const [deletingMessage, setDeletingMessage] = useState<Message | null>(null);
    const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [typingUsers, setTypingUsers] = useState<Record<string, { username: string, pfp: string }>>({});
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [dragOffsets, setDragOffsets] = useState<Record<number, number>>({});
    const dragStartXRef = useRef<Record<number, number>>({});
    const dragStartYRef = useRef<Record<number, number>>({});
    const longPressTimeoutRef = useRef<Record<number, NodeJS.Timeout | null>>({});
    const lastReactionTimeRef = useRef<Record<number, number>>({});
    const { mentionQuery, updateSearch, completeMention } = useMentionSearch();

    const [messageInput, setMessageInput] = useState('');
    const [isPhone, setIsPhone] = useState(window.innerWidth <= 768);
    useEffect(() => {
        setShowMobilePreview(false);
    }, [activeConversationId, activeSystemModId]);


    const [selectedFontId, setSelectedFontId] = useState<string>('default');
    const [showThemeModal, setShowThemeModal] = useState(false);
    useScrollLock(!!forwardingMessage || !!deletingMessage || showThemeModal || !!showEmojiPickerFor || !!activeMenu);

    useEffect(() => {
        onInternalModalStateChange?.(!!(forwardingMessage || deletingMessage || showThemeModal || showEmojiPickerFor || activeMenu));
    }, [forwardingMessage, deletingMessage, showThemeModal, showEmojiPickerFor, activeMenu, onInternalModalStateChange]);

    useEffect(() => {
        if (closeInternalModalsTrigger > 0) {
            setForwardingMessage(null);
            setDeletingMessage(null);
            setShowThemeModal(false);
            setShowEmojiPickerFor(null);
            setActiveMenu(null);
        }
    }, [closeInternalModalsTrigger]);


    const [recentEmojis, setRecentEmojis] = useState<string[]>(() => {
        const saved = localStorage.getItem('recent_emojis');
        return saved ? JSON.parse(saved) : DEFAULT_EMOJI_LIST;
    });

    const swipeStartX = useRef<number | null>(null);
    const swipeStartY = useRef<number | null>(null);
    const hasSwiped = useRef<boolean>(false);

    useEffect(() => {
        const handleResize = () => setIsPhone(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (e.touches.length === 1) {
            swipeStartX.current = e.touches[0].clientX;
            swipeStartY.current = e.touches[0].clientY;
            hasSwiped.current = false;
        }
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (swipeStartX.current !== null && swipeStartY.current !== null && onSwipeTransition && !hasSwiped.current) {
            const currentX = e.touches[0].clientX;
            const currentY = e.touches[0].clientY;
            const deltaX = currentX - swipeStartX.current;
            const deltaY = currentY - swipeStartY.current;

            if (Math.abs(deltaX) > 30 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
                const target = e.target as HTMLElement;
                const isInteractive = target.closest('button, a, input, textarea, [role="button"], .message-bubble, .message-drag-target');

                if (!isInteractive) {
                    onSwipeTransition(deltaX > 0 ? 'right' : 'left');
                    hasSwiped.current = true;
                }
            }
        }
    }, [onSwipeTransition]);

    // Handle initial shared content
    useEffect(() => {
        if (initialSharedContent && activeConversationId) {
            if (initialSharedContent.text) setMessageInput(initialSharedContent.text);
            if (initialSharedContent.media && initialSharedContent.media.length > 0) {
                const newAttachments: Attachment[] = initialSharedContent.media.map(m => ({
                    url: m.url,
                    type: m.type as any,
                    width: m.width,
                    height: m.height
                }));
                setAttachments(prev => [...prev, ...newAttachments]);
            }
            if (clearSharedContent) clearSharedContent();
        }
    }, [initialSharedContent, activeConversationId, clearSharedContent]);

    const handleTouchEnd = useCallback(() => {
        swipeStartX.current = null;
        swipeStartY.current = null;
    }, []);

    const updateRecentEmojis = (emoji: string) => {
        setRecentEmojis(prev => {
            const next = [emoji, ...prev.filter(e => e !== emoji)].slice(0, 20);
            localStorage.setItem('recent_emojis', JSON.stringify(next));
            return next;
        });
    };

    const anonymousAliases = useMemo(() => {
        if (!activeConversation || activeConversation.yipYapMode !== 'anonymous') return {};

        const aliases: Record<string, { name: string, pfp: string }> = {};
        const participants = Array.from(new Set([
            ...activeConversation.participants,
            ...activeConversation.messages.map(m => m.senderId)
        ]));

        participants.forEach(senderId => {
            // Deterministic but random-looking alias based on senderId hash
            let hash = 0;
            for (let i = 0; i < senderId.length; i++) {
                hash = ((hash << 5) - hash) + senderId.charCodeAt(i);
                hash |= 0;
            }
            const num = Math.abs(hash % 10000).toString().padStart(4, '0');
            const spriteType = ['bottts', 'adventurer', 'avataaars', 'pixel-art'][Math.abs(hash % 4)];

            aliases[senderId] = {
                name: `yipyapper#${num}`,
                pfp: `https://api.dicebear.com/7.x/${spriteType}/svg?seed=${senderId}&backgroundColor=b6e3f4,c0aede,d1d4f9`
            };
        });
        return aliases;
    }, [activeConversation]);

    const handleMessageDragStart = (e: React.TouchEvent | React.MouseEvent, msgId: number) => {
        const target = e.target as HTMLElement;
        const msg = messagesById.get(msgId);
        const isCardMsg = msg && (Boolean(msg.sharedPostId) || Boolean(msg.linkRequestDetails?.isLinkRequest) || msg.metadata?.type === 'link_request' || (Boolean(msg.text) && msg.text.includes('/profile/')));

        if (isCardMsg) {
            // For card messages, drag/hold MUST originate from inside the actual card element
            const touchedCard = target.closest('.dynamic-shared-post-card');
            if (!touchedCard) return;
        } else {
            // For standard text/media messages, ignore interactive buttons/controls but still allow media previews to trigger the hold menu.
            if (target.closest('button, a, textarea, input')) return;
        }

        // Only allow one drag animation at once
        if (Object.keys(dragStartXRef.current).length > 0) return;

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        dragStartXRef.current[msgId] = clientX;
        dragStartYRef.current[msgId] = clientY;

        if (longPressTimeoutRef.current[msgId]) clearTimeout(longPressTimeoutRef.current[msgId]!);
        longPressTimeoutRef.current[msgId] = setTimeout(() => {
            setActiveMenu(msgId);
        }, 500);
    };

    const handleMessageDragMove = (e: React.TouchEvent | React.MouseEvent, msgId: number) => {
        const startX = dragStartXRef.current[msgId];
        const startY = dragStartYRef.current[msgId];
        if (startX === undefined || startY === undefined) return;

        const currentX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const currentY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const diffX = currentX - startX;
        const diffY = currentY - startY;

        // Cancel long press if moved significantly in ANY direction (scrolling or dragging)
        if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
            if (longPressTimeoutRef.current[msgId]) {
                clearTimeout(longPressTimeoutRef.current[msgId]!);
                longPressTimeoutRef.current[msgId] = null;
            }
        }

        // Only handle horizontal drag for the reply action if it's primarily horizontal
        if (Math.abs(diffX) > 8 && Math.abs(diffX) > Math.abs(diffY)) {
            const msg = messagesById.get(msgId);
            const isOwn = msg?.senderId === currentUserId;
            
            let limitedDiff = 0;
            if ((isOwn && diffX < 0) || (!isOwn && diffX > 0)) {
                limitedDiff = Math.sign(diffX) * Math.min(Math.abs(diffX), MAX_DRAG_DISTANCE);
            }
            
            setDragOffsets(prev => ({ ...prev, [msgId]: limitedDiff }));
        }
    };

    const handleMessageDragEnd = (msgId: number) => {
        const offset = dragOffsets[msgId] || 0;
        const msg = messagesById.get(msgId);
        const REPLY_THRESHOLD = 25;

        if (Math.abs(offset) >= REPLY_THRESHOLD && msg) {
            const isOwn = (msg.senderId || (msg as any).authorId) === currentUserId;
            if (isOwn) {
                if (offset < -REPLY_THRESHOLD) setReplyingTo(msg);
            } else {
                if (offset > REPLY_THRESHOLD) setReplyingTo(msg);
            }
        }

        setDragOffsets(prev => {
            const next = { ...prev };
            delete next[msgId];
            return next;
        });
        delete dragStartXRef.current[msgId];
        if (longPressTimeoutRef.current[msgId]) {
            clearTimeout(longPressTimeoutRef.current[msgId]!);
            longPressTimeoutRef.current[msgId] = null;
        }
    };



    const scrollToMessage = (msgId: number) => {
        const element = document.getElementById(`message-${msgId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('highlight-message');
            setTimeout(() => element.classList.remove('highlight-message'), 2000);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setActiveMenu(null);
                if (!(event.target as HTMLElement).closest('[data-emoji-picker]')) {
                    setShowEmojiPickerFor(null);
                }
            }
        };

        const handleTypingStatus = (data: { room: string, username: string, isTyping: boolean, pfp: string }) => {
            if (data.room === `conversation:${activeConversationId}` && data.username !== currentUserId) {
                setTypingUsers(prev => {
                    const next = { ...prev };
                    if (data.isTyping) {
                        next[data.username] = { username: data.username, pfp: data.pfp };
                    } else {
                        delete next[data.username];
                    }
                    return next;
                });
            }
        };

        if (socket && activeConversationId) {
            socket.on('typing-status', handleTypingStatus);
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            if (socket) {
                socket.off('typing-status', handleTypingStatus);
            }
        };
    }, [activeConversationId, currentUserId, socket]);

    const handlePaste = async (e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        const files: File[] = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                const file = items[i].getAsFile();
                if (file) {
                    files.push(file);
                }
            }
        }

        if (files.length > 0) {
            e.preventDefault();
            for (const file of files) {
                try {
                    let processedFile = file;
                    let mediaType: MediaType = 'file';

                    const typeRaw = file.type.split('/')[0];
                    if (typeRaw === 'video') mediaType = 'video';
                    else if (typeRaw === 'audio') mediaType = 'audio';
                    else if (typeRaw === 'image') mediaType = 'image';

                    if (mediaType === 'video') {
                        const blob = await compressVideo(file);
                        processedFile = new File([blob], file.name, { type: file.type });
                    } else if (mediaType === 'audio') {
                        const blob = await compressAudio(file);
                        processedFile = new File([blob], file.name, { type: file.type });
                    } else if (mediaType === 'image') {
                        const blob = await compressImage(file);
                        processedFile = new File([blob], file.name, { type: file.type });
                    }

                    const attachment: Attachment = {
                        url: URL.createObjectURL(processedFile),
                        type: mediaType,
                        file: processedFile
                    };
                    setAttachments(prev => [...prev, attachment]);
                } catch (err) {
                    console.error('Error pasting file:', err);
                }
            }
        }
    };

    const messagesById = useMemo(() => {
        const map = new Map<number, Message>();
        activeConversation?.messages.forEach(msg => map.set(msg.id, msg));
        return map;
    }, [activeConversation]);

    const sortedConversations = useMemo(() => {
        const rawFiltered = conversations.filter(c => activeTab === 'yipyap' ? c.isYipYap : !c.isYipYap);

        // Deduplicate 1-on-1 DM conversations so the same recipient never appears twice in the list
        const deduplicated: Conversation[] = [];
        const seenPairs = new Map<string, number>();

        for (const c of rawFiltered) {
            if (!c.isYipYap && c.participants && c.participants.length === 2) {
                const pairKey = [...c.participants].map(p => p.toLowerCase()).sort().join('::');
                if (seenPairs.has(pairKey)) {
                    const existingIdx = seenPairs.get(pairKey)!;
                    const existingConvo = deduplicated[existingIdx];

                    // Merge messages from duplicate conversation objects
                    const combinedMsgs = [...(existingConvo.messages || []), ...(c.messages || [])];
                    const msgMap = new Map<string | number, Message>();
                    combinedMsgs.forEach(m => {
                        const key = m.id || `${m.senderId}_${m.timestamp}_${m.text}`;
                        msgMap.set(key, m);
                    });
                    const uniqueMsgs = Array.from(msgMap.values()).sort((a, b) =>
                        new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime()
                    );
                    deduplicated[existingIdx] = {
                        ...existingConvo,
                        messages: uniqueMsgs
                    };
                    continue;
                }
                seenPairs.set(pairKey, deduplicated.length);
            }
            deduplicated.push(c);
        }

        return deduplicated.sort((a, b) => {
            const aPinned = a.pinnedBy?.includes(currentUserId);
            const bPinned = b.pinnedBy?.includes(currentUserId);
            if (aPinned && !bPinned) return -1;
            if (!aPinned && bPinned) return 1;

            const lastA = a.messages[a.messages.length - 1];
            const lastB = b.messages[b.messages.length - 1];
            if (!lastA && !lastB) return 0;
            if (!lastA) return 1;
            if (!lastB) return -1;
            const timeA = lastA.timestamp ? new Date(lastA.timestamp).getTime() : 0;
            const timeB = lastB.timestamp ? new Date(lastB.timestamp).getTime() : 0;
            const validTimeA = isNaN(timeA) ? 0 : timeA;
            const validTimeB = isNaN(timeB) ? 0 : timeB;
            
            return validTimeB - validTimeA;
        });
    }, [conversations, activeTab, currentUserId]);

    const communityChannels = useMemo(() => {
        const channels: { community: Community, channel: Channel }[] = [];
        communities.forEach(c => {
            (c.channels || []).filter(chan => chan.type === 'chat').forEach(chan => {
                channels.push({ community: c, channel: chan });
            });
        });
        // TODO: Sort by lastMessageTimestamp when available
        return channels.sort((a, b) => a.community.name.localeCompare(b.community.name) || a.channel.name.localeCompare(b.channel.name));
    }, [communities]);

    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [showMessageRecorder, setShowMessageRecorder] = useState(false);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraMode, setCameraMode] = useState<'image' | 'video' | 'both'>('both');
    const audioRecorderRef = useRef<AudioRecorderHandle>(null);

    const [showTenorModal, setShowTenorModal] = useState(false);
    const [showInputMenu, setShowInputMenu] = useState(false);

    const handleAttachTenorGif = (gifUrl: string) => {
        const existingIsAudio = attachments.length > 0 && (attachments[0].type === 'audio' || attachments[0].type === 'voice');
        if (existingIsAudio) {
            alert("You can't mix photos/videos with audio files in the same message.");
            return;
        }

        if (attachments.length + 1 > 10) {
            alert("You can only upload up to 10 photos/videos per message.");
            return;
        }

        setAttachments(prev => [...prev, { url: gifUrl, type: 'image' }]);
        setShowTenorModal(false);
        setShowInputMenu(false);
    };

    const handleCameraCapture = (file: File) => {
        const pseudoEvent = {
            target: {
                files: [file]
            }
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleMediaUpload(pseudoEvent, true);
    };

    const handleGalleryClick = async () => {
        if (Capacitor.isNativePlatform()) {
            try {
                const checkStatus = await Camera.checkPermissions();
                if (checkStatus.photos !== 'granted' && checkStatus.photos !== 'limited') {
                    const reqStatus = await Camera.requestPermissions({ permissions: ['photos'] });
                    if (reqStatus.photos !== 'granted' && reqStatus.photos !== 'limited') {
                        throw new Error('Photo library permission denied');
                    }
                }
                const result = await Camera.pickImages({
                    quality: 100,
                    limit: 10
                });
                if (result.photos && result.photos.length > 0) {
                    const files = await Promise.all(result.photos.map(async (p, idx) => {
                        const response = await fetch(p.webPath!);
                        const blob = await response.blob();
                        const extension = p.format || 'jpeg';
                        const filename = `gallery-${Date.now()}-${idx}.${extension}`;
                        return new File([blob], filename, { type: blob.type || `image/${extension}` });
                    }));
                    const pseudoEvent = {
                        target: {
                            files: files
                        }
                    } as unknown as React.ChangeEvent<HTMLInputElement>;
                    handleMediaUpload(pseudoEvent);
                }
            } catch (err) {
                console.error("Native gallery pick failed, falling back to file input", err);
                document.getElementById('msg-file')?.click();
            }
        } else {
            document.getElementById('msg-file')?.click();
        }
    };

    const handleMessageRecorderComplete = async (audioUrl: string, blob: Blob) => {
        try {
            let extension = 'webm';
            let mimeType = blob.type || 'audio/webm';
            if (blob.type) {
                if (blob.type.includes('mp4')) {
                    extension = 'm4a';
                    mimeType = 'audio/mp4';
                } else if (blob.type.includes('aac')) {
                    extension = 'aac';
                    mimeType = 'audio/aac';
                } else if (blob.type.includes('ogg')) {
                    extension = 'ogg';
                    mimeType = 'audio/ogg';
                } else if (blob.type.includes('wav')) {
                    extension = 'wav';
                    mimeType = 'audio/wav';
                } else if (blob.type.includes('webm')) {
                    extension = 'webm';
                    mimeType = 'audio/webm';
                }
            } else {
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                if (isIOS) {
                    extension = 'm4a';
                    mimeType = 'audio/mp4';
                }
            }
            const file = new File([blob], `voice-message-${Date.now()}.${extension}`, { type: mimeType });
            const tempUrl = URL.createObjectURL(file);
            setAttachments(prev => [...prev, { url: tempUrl, type: 'voice', file: file }]);
            setShowMessageRecorder(false);
            if (audioStream) {
                audioStream.getTracks().forEach(track => track.stop());
                setAudioStream(null);
            }
        } catch (error) {
            console.error("Failed to process voice message", error);
        }
    };

    const handleMessageRecorderReset = () => {
        setShowMessageRecorder(false);
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
            setAudioStream(null);
        }
    };

    const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>, isLive?: boolean) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > 500 * 1024 * 1024) {
            alert('File size exceeds the 500MB limit. Please choose a smaller file.');
            if (e.target) e.target.value = '';
            return;
        }

        setIsUploadingMedia(true);

        try {
            const processedAttachments = await Promise.all(files.map(async (file) => {
                let processedFile = file;
                let type: MediaType = 'file';
                const typeRaw = file.type.split('/')[0];
                if (typeRaw === 'video') type = 'video';
                else if (typeRaw === 'audio') type = 'audio';
                else if (typeRaw === 'image') type = 'image';

                if (type === 'video') {
                    const blob = await compressVideo(file);
                    processedFile = new File([blob], file.name, { type: file.type });
                } else if (type === 'audio') {
                    const blob = await compressAudio(file);
                    processedFile = new File([blob], file.name, { type: file.type });
                } else if (type === 'image') {
                    const blob = await compressImage(file);
                    processedFile = new File([blob], file.name, { type: file.type });
                }
                return { url: URL.createObjectURL(processedFile), type, file: processedFile, isLive };
            }));
            setAttachments(prev => [...prev, ...processedAttachments]);
        } catch (error) {
            console.error('Failed to prepare media:', error);
        } finally {
            setIsUploadingMedia(false);
            e.target.value = '';
        }
    };

    const removeAttachment = (index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSendMessage = (e: React.FormEvent) => {
        e.preventDefault();
        const text = messageInput.trim();
        if ((text || attachments.length > 0) && activeConversation) {
            onSendMessage(activeConversation.id, text, replyingTo?.id, [...attachments], undefined, selectedFontId);
            setAttachments([]);
            setReplyingTo(null);
            setMessageInput('');
            if (textareaRef.current) textareaRef.current.style.height = 'auto';
        }
    };

    const emitTypingStatus = (isTyping: boolean) => {
        if (!socket || !activeConversationId) return;
        socket.emit('typing-status', {
            room: `conversation:${activeConversationId}`,
            username: currentUserId,
            isTyping,
            pfp: usersData[currentUserId]?.pfp || ''
        });
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setMessageInput(value);
        updateSearch(value, e.target.selectionStart);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        emitTypingStatus(value.length > 0);
        typingTimeoutRef.current = setTimeout(() => emitTypingStatus(false), 3000);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    };

    const handleMessageKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && window.innerWidth > 768) {
            e.preventDefault();
            handleSendMessage(e as any);
        }
    };

    const handleSaveEdit = async (messageId: number, text: string) => {
        if (!activeConversationId || !text.trim()) return;
        try {
            await editDirectMessageOnServer(activeConversationId, messageId, currentUserId, text.trim());
            setEditingMessageId(null);
            setEditText('');
        } catch (error) {
            console.error("Failed to edit message:", error);
        }
    };

    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!activeConversation) return;

        const lastMsg = activeConversation.messages[activeConversation.messages.length - 1];
        const wasSentByMe = lastMsg?.senderId === currentUserId;

        if (wasSentByMe || isAtBottom) {
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeConversation?.messages.length, currentUserId]);

    return (
        <div
            className="flex h-full w-full overflow-hidden bg-transparent"
        >
            {/* Modal Components */}
            {forwardingMessage && <ForwardMessageModal isOpen={!!forwardingMessage} onClose={() => setForwardingMessage(null)} message={forwardingMessage} conversations={conversations} currentUserId={currentUserId} onForward={onForwardMessage} usersData={usersData} />}
            {deletingMessage && <DeleteMessageModal isOpen={!!deletingMessage} onClose={() => setDeletingMessage(null)} onConfirm={() => { onDeleteMessage(activeConversationId!, deletingMessage.id); setDeletingMessage(null); }} />}
            {deletingConversationId && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setDeletingConversationId(null)}>
                    <div className="w-full max-w-sm bg-[#1b1b1f] border border-white/10 rounded-3xl p-6 text-center" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-white mb-2">Delete Conversation</h3>
                        <p className="text-sm text-gray-400 mb-6">Are you sure you want to delete this conversation? This will hide it from your list.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setDeletingConversationId(null)} className="flex-1 py-3 rounded-xl bg-white/10 text-white font-bold hover:bg-white/20 transition-colors">Cancel</button>
                            <button onClick={() => { if (onDeleteConversation) onDeleteConversation(deletingConversationId); setDeletingConversationId(null); }} className="flex-1 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-500 transition-colors">Delete</button>
                        </div>
                    </div>
                </div>
            )}
            {activeConversation && (
                <ConversationThemeModal
                    isOpen={showThemeModal}
                    onClose={() => setShowThemeModal(false)}
                    activeConversation={activeConversation}
                    onUpdateConversation={(updates) => onUpdateConversation(activeConversation.id, updates)}
                    currentUser={currentUserId}
                    usersData={usersData}
                    onDeleteConversation={onDeleteConversation || (() => {})}
                />
            )}
            {showEmojiPickerFor && (
                <EmojiPickerModal
                    isOpen={true}
                    onClose={() => setShowEmojiPickerFor(null)}
                    onEmojiSelect={(emoji) => {
                        updateRecentEmojis(emoji);
                        if (showEmojiPickerFor === 'input') {
                            setMessageInput(prev => prev + emoji);
                        } else if (activeConversationId) {
                            onAddReaction(activeConversationId, showEmojiPickerFor as number, emoji);
                        }
                        setShowEmojiPickerFor(null);
                    }}
                />
            )}
            {activeMenu && activeConversation && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveMenu(null)}>
                    <div className="w-full max-w-xs bg-[#1b1b1f] border border-white/10 rounded-3xl shadow-2xl overflow-hidden p-2" onClick={e => e.stopPropagation()}>
                        {(() => {
                            const msg = activeConversation.messages.find(m => m.id === activeMenu);
                            if (!msg) return null;
                            const isOwn = (msg.senderId || (msg as any).authorId) === currentUserId;

                            return (
                                <>
                                    <button onClick={() => { setReplyingTo(msg); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                        <ReplyIcon className="w-4 h-4 text-blue-400" />
                                        <span>Reply</span>
                                    </button>
                                    <button onClick={() => { setForwardingMessage(msg); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                        <ForwardIcon className="w-4 h-4 text-purple-400" />
                                        <span>Forward</span>
                                    </button>
                                    <button onClick={() => { navigator.clipboard.writeText(msg.text || ''); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                        <CopyIcon className="w-4 h-4 text-green-400" />
                                        <span>Copy Text</span>
                                    </button>
                                    {msg.media && msg.media.length > 0 && (
                                        <button onClick={() => { const firstMedia = msg.media?.[0]; if (firstMedia) downloadMediaUrl(firstMedia.url, firstMedia.file?.name || `${firstMedia.type}-attachment`); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                            <DownloadIcon className="w-4 h-4 text-cyan-400" />
                                            <span>Save Media</span>
                                        </button>
                                    )}
                                    {isOwn && (
                                        <button onClick={() => { setEditingMessageId(msg.id); setEditText(msg.text || ''); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                            <EditIcon className="w-4 h-4 text-orange-400" />
                                            <span>Edit Message</span>
                                        </button>
                                    )}
                                    <button onClick={() => { setDeletingMessage(msg); setActiveMenu(null); }} className="w-full flex items-center gap-3 p-3 hover:bg-red-500/10 text-red-400 rounded-2xl text-sm transition-colors">
                                        <TrashIcon className="w-4 h-4" />
                                        <span>Delete</span>
                                    </button>
                                </>
                            );
                        })()}
                        <div className="p-3 border-t border-white/5 mt-2 flex justify-between gap-1 overflow-x-auto">
                            {recentEmojis.slice(0, 6).map(emoji => (
                                <button
                                    key={emoji}
                                    onClick={() => {
                                        onAddReaction(activeConversationId!, activeMenu!, emoji);
                                        updateRecentEmojis(emoji);
                                        setActiveMenu(null);
                                    }}
                                    className="text-xl hover:scale-125 transition-transform flex items-center justify-center"
                                >
                                    <span className="sticker-outline">{emoji}</span>
                                </button>
                            ))}
                            <button onClick={() => { setShowEmojiPickerFor(activeMenu!); setActiveMenu(null); }} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-gray-400"><PlusIcon className="w-4 h-4" /></button>
                        </div>
                    </div>
                </div>
            )}

            {conversationMenuId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setConversationMenuId(null)}>
                    <div className="w-full max-w-xs bg-[#1b1b1f] border border-white/10 rounded-3xl shadow-2xl overflow-hidden p-2" onClick={e => e.stopPropagation()}>
                        {(() => {
                            const convo = conversations.find(c => c.id === conversationMenuId);
                            if (!convo) return null;
                            const isPinned = convo.pinnedBy?.includes(currentUserId);
                            const isMuted = convo.mutedBy?.includes(currentUserId);
                            
                            const lastMessage = convo.messages[convo.messages.length - 1];
                            const defaultUnread = lastMessage && lastMessage.senderId !== currentUserId && !lastMessage.isRead;
                            const explicitUnread = convo.unreadBy?.includes(currentUserId);
                            const currentlyUnread = explicitUnread || (defaultUnread && !convo.unreadBy?.includes(currentUserId)); // If explicitly unread, or default unread

                            const isDirectMessage = !convo.isYipYap && convo.participants.length === 2;
                            const otherUserId = isDirectMessage ? convo.participants.find(p => p !== currentUserId) : null;

                            return (
                                <>
                                    <button onClick={() => { 
                                        const newPinned = isPinned ? (convo.pinnedBy || []).filter(u => u !== currentUserId) : [...(convo.pinnedBy || []), currentUserId];
                                        onUpdateConversation(convo.id, { pinnedBy: newPinned } as any);
                                        import('../api').then(({ togglePinConversationOnServer }) => togglePinConversationOnServer(convo.id, currentUserId, !isPinned));
                                        setConversationMenuId(null); 
                                    }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                        <span className="text-lg w-5 text-center">{isPinned ? '📌' : '📍'}</span>
                                        <span>{isPinned ? 'Unpin' : 'Pin'} Conversation</span>
                                    </button>
                                    <button onClick={() => { 
                                        const newMuted = isMuted ? (convo.mutedBy || []).filter(u => u !== currentUserId) : [...(convo.mutedBy || []), currentUserId];
                                        onUpdateConversation(convo.id, { mutedBy: newMuted } as any);
                                        import('../api').then(({ toggleMuteConversationOnServer }) => toggleMuteConversationOnServer(convo.id, currentUserId, !isMuted));
                                        setConversationMenuId(null); 
                                    }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                        <span className="text-lg w-5 text-center">{isMuted ? '🔊' : '🔕'}</span>
                                        <span>{isMuted ? 'Unmute' : 'Mute'}</span>
                                    </button>
                                    <button onClick={() => { 
                                        const newUnread = currentlyUnread ? (convo.unreadBy || []).filter(u => u !== currentUserId) : [...(convo.unreadBy || []), currentUserId];
                                        onUpdateConversation(convo.id, { unreadBy: newUnread } as any);
                                        import('../api').then(({ toggleUnreadConversationOnServer }) => toggleUnreadConversationOnServer(convo.id, currentUserId, !currentlyUnread));
                                        setConversationMenuId(null); 
                                    }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                        <span className="text-lg w-5 text-center">{currentlyUnread ? '📖' : '🔵'}</span>
                                        <span>{currentlyUnread ? 'Mark as Read' : 'Mark as Unread'}</span>
                                    </button>
                                    {isDirectMessage && otherUserId && onOpenCreateYipYapWithUser && (
                                        <button onClick={() => { 
                                            onOpenCreateYipYapWithUser(otherUserId);
                                            setConversationMenuId(null); 
                                        }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                            <span className="text-lg w-5 text-center">📢</span>
                                            <span>Create YipYap with user</span>
                                        </button>
                                    )}
                                    {isDirectMessage && otherUserId && onOpenCreateCommunityWithUser && (
                                        <button onClick={() => { 
                                            onOpenCreateCommunityWithUser(otherUserId);
                                            setConversationMenuId(null); 
                                        }} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white">
                                            <span className="text-lg w-5 text-center">🏘️</span>
                                            <span>Create Community with user</span>
                                        </button>
                                    )}
                                    <div className="h-px bg-white/10 my-1"></div>
                                    <button onClick={() => { 
                                        setDeletingConversationId(convo.id);
                                        setConversationMenuId(null); 
                                    }} className="w-full flex items-center gap-3 p-3 hover:bg-red-500/10 text-red-400 rounded-2xl text-sm transition-colors">
                                        <TrashIcon className="w-4 h-4 ml-0.5" />
                                        <span className="ml-0.5">Delete</span>
                                    </button>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}


            {/* Sidebar with App Theme Background */}
            <div className={`${activeConversation && isPhone ? 'hidden' : 'flex'} w-full md:w-80 h-full flex-col flex-shrink-0 relative z-30`}
                style={{
                    backgroundColor: (() => {
                        const user = usersData[currentUserId];
                        const baseColor = user?.appThemeColor || '#111';
                        const opacity = (postBlurOpacity ?? 80) / 100;
                        return hexToRgba(baseColor, opacity) || `rgba(20, 20, 30, ${opacity})`;
                    })()
                }}>
                <div className="p-4 pt-[calc(2rem+var(--safe-area-top,0px))] flex-shrink-0">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className={`text-xl font-bold ${isPhone ? 'pl-10' : ''}`} style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.3) }}>Messages</h2>
                        <button 
                            onClick={() => {
                                if (activeTab === 'dm') {
                                    if (onOpenNewMessage) onOpenNewMessage();
                                } else if (activeTab === 'yipyap') {
                                    if (onOpenCreateYipYap) onOpenCreateYipYap();
                                } else if (activeTab === 'community') {
                                    if (onOpenCreateCommunity) onOpenCreateCommunity();
                                }
                            }}
                            className="p-2 hover:bg-white/5 rounded-full text-white/70 transition-colors active:scale-95"
                        >
                            <PlusIcon className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex bg-black/40 p-1 rounded-2xl border border-white/10 shadow-inner">
                        <button
                            onClick={() => setActiveTab('dm')}
                            className={`flex-1 py-2 px-4 rounded-xl text-xs font-bold transition-all ${activeTab === 'dm' ? 'bg-blue-600 text-white shadow-lg' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
                        >
                            DIRECT
                        </button>
                        <button
                            onClick={() => setActiveTab('yipyap')}
                            className={`flex-1 py-2 px-4 rounded-xl text-xs font-bold transition-all ${activeTab === 'yipyap' ? 'bg-blue-600 text-white shadow-lg' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
                        >
                            YIPYAP
                        </button>
                        <button
                            onClick={() => setActiveTab('community')}
                            className={`flex-1 py-2 px-4 rounded-xl text-xs font-bold transition-all ${activeTab === 'community' ? 'bg-blue-600 text-white shadow-lg' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
                        >
                            COMMUNITY
                        </button>
                    </div>
                </div>

                <div className="flex-grow overflow-y-auto p-2 pb-[calc(7rem+var(--safe-area-bottom,0px))] space-y-1 custom-scrollbar">
                    {activeTab === 'dm' || activeTab === 'yipyap' ? (
                        sortedConversations.map(convo => {
                            const isYipYap = convo.isYipYap;
                            const otherUserId = isYipYap ? (convo.yipYapOwner || '') : (convo.participants.find(p => p !== currentUserId) || '');
                            const otherUser = usersData[otherUserId];

                            // For regular DMs, skip if user data is missing. 
                            // For YipYaps, we can render with basic info.
                            if (!otherUser && !isYipYap) return null;

                            const nickname = isYipYap ? (convo.yipYapName || 'Anonymous Hall') : (convo.nicknames?.[otherUserId] || otherUser?.displayName || otherUserId);
                            const lastMessage = convo.messages[convo.messages.length - 1];
                            const isUnread = lastMessage && lastMessage.senderId !== currentUserId && !lastMessage.isRead;

                            const fallbackPfp = `https://api.dicebear.com/7.x/bottts/svg?seed=${otherUserId || 'anon'}&backgroundColor=b6e3f4`;
                            const pfp = isYipYap && convo.yipYapMode === 'anonymous'
                                ? 'https://api.dicebear.com/7.x/bottts/svg?seed=anonymous&backgroundColor=b6e3f4'
                                : (otherUser?.pfp || fallbackPfp);

                            return (
                                <div key={convo.id} 
                                    onClick={() => { onSelectConversation(convo.id); setSidebarOpen(false); }}
                                    onContextMenu={(e) => { e.preventDefault(); setConversationMenuId(convo.id); }}
                                    onTouchStart={() => { pressTimerRef.current = setTimeout(() => setConversationMenuId(convo.id), 500); }}
                                    onTouchEnd={() => { if (pressTimerRef.current) clearTimeout(pressTimerRef.current); }}
                                    onTouchMove={() => { if (pressTimerRef.current) clearTimeout(pressTimerRef.current); }}
                                    className={`w-full rounded-xl text-left p-3 flex items-center gap-3 transition-all cursor-pointer ${activeConversationId === convo.id ? 'bg-blue-600/20 shadow-inner' : 'hover:bg-white/5 hover:translate-x-1'}`}>
                                    <div 
                                        className={`relative flex-shrink-0 ${otherUserId && (!isYipYap || convo.yipYapMode !== 'anonymous') ? 'hover:scale-105 transition-transform' : ''}`}
                                        onClick={(e) => {
                                            if (otherUserId && (!isYipYap || convo.yipYapMode !== 'anonymous')) {
                                                e.stopPropagation();
                                                onNavigateToProfile?.(otherUserId);
                                            }
                                        }}
                                    >
                                        <img
                                            src={pfp}
                                            className="w-12 h-12 rounded-full object-cover"
                                            alt=""
                                        />
                                        {isYipYap && (
                                            <div className="absolute -bottom-1 -right-1 bg-black rounded-full p-0.5 text-[10px]">
                                                {convo.yipYapMode === 'anonymous' ? '👻' : '📢'}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-grow min-w-0">
                                        <div className="flex justify-between items-center">
                                            <span className="font-bold truncate" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.25) }}>{nickname}</span>
                                            <div className="flex items-center gap-1">
                                                {convo.pinnedBy?.includes(currentUserId) && <span className="text-[10px]">📌</span>}
                                                {convo.mutedBy?.includes(currentUserId) && <span className="text-[10px]">🔕</span>}
                                                {(isUnread || convo.unreadBy?.includes(currentUserId)) && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.6)]"></div>}
                                            </div>
                                        </div>
                                        <p className={`text-xs truncate`} style={{ ...((isUnread || convo.unreadBy?.includes(currentUserId)) ? { color: 'white', fontWeight: 600, ...stickerTextStyle(0.2) } : { color: 'rgba(255,255,255,0.6)' }) }}>
                                            {lastMessage ? (lastMessage.senderId === currentUserId ? 'You: ' : '') + (lastMessage.text || '📷 Attachment') : 'No messages yet'}
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        communityChannels.map(({ community, channel }) => (
                            <div key={`${community._id}-${channel.id}`} onClick={() => onSelectCommunityChannel?.(community._id, channel.id)}
                                className="w-full rounded-xl text-left p-3 flex items-center gap-3 transition-all cursor-pointer hover:bg-white/5 hover:translate-x-1">
                                {community.avatar ? (
                                    <img src={community.avatar} className="w-12 h-12 rounded-xl object-cover shadow-md" alt="" />
                                ) : (
                                    <div className="w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-400 font-bold">
                                        {community.name[0]}
                                    </div>
                                )}
                                <div className="flex-grow min-w-0">
                                    <div className="flex justify-between items-center">
                                        <span className="font-bold truncate" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.25) }}>{community.name}</span>
                                    </div>
                                    <p className="text-xs truncate text-white/60">
                                        <HashtagIcon className="w-3 h-3 inline mr-1 opacity-50" />
                                        {channel.name}
                                    </p>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Chat Window Area and Preview Side-by-Side */}
            <div className="flex-grow flex h-full min-w-0">
                <div className={`${(!activeConversation || (isPhone && showMobilePreview)) && isPhone ? 'hidden' : 'flex'} flex-grow flex-col h-full relative overflow-hidden`}
                    style={{
                        backgroundColor: (() => {
                            const baseColor = activeConversation?.theme?.backgroundColor || '#0a0a0b';
                            const opacity = Math.min(85, postBlurOpacity ?? 80) / 100;
                            return hexToRgba(baseColor, opacity) || baseColor;
                        })(),
                    }}>
                    {/* Mod Editing Mode Overlay/Background */}
                    {(() => {
                        const isSystemChat = activeConversation?.participants.includes('goodlyfe');
                        const isModEditingMode = isSystemChat && activeSystemModId;
                        if (!isModEditingMode) return null;
                        return (
                            <div className="absolute inset-0 bg-blue-900/10 pointer-events-none overflow-hidden">
                                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
                            </div>
                        );
                    })()}
                    {/* Background Slideshow Layer */}
                    {activeConversation?.theme?.backgroundMode === 'image' && (() => {
                        const slideshow = activeConversation.theme.backgroundSlideshow;
                        let slideUrl = '';
                        let isTile = activeConversation.theme.backgroundImageStyle === 'tile';
                        let slideResolution = activeConversation.theme.backgroundResolution || 100;

                        if (slideshow?.attachments && slideshow.attachments.length > 0) {
                            const attachments = slideshow.attachments;
                            const sIdx = bgSlideshowIdx % attachments.length;
                            const slide = attachments[sIdx];
                            if (slide && slide.url) {
                                slideUrl = slide.url;
                                isTile = slide.style === 'tile' ? true : isTile;
                                slideResolution = slide.resolution || slideResolution;
                            }
                        } else if (activeConversation.theme.backgroundImage) {
                            slideUrl = activeConversation.theme.backgroundImage;
                        }

                        if (!slideUrl) return null;
                        return (
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    backgroundImage: `url(${slideUrl})`,
                                    backgroundRepeat: isTile ? 'repeat' : 'no-repeat',
                                    backgroundSize: isTile
                                        ? `${slideResolution}vw`
                                        : 'cover',
                                    backgroundPosition: 'center',
                                    opacity: 1
                                }}
                            />
                        );
                    })()}

                    {activeConversation ? (
                        <>
                            {/* Sticky Header */}
                            {(() => {
                                const otherUserId = activeConversation.participants.find(p => p !== currentUserId) || '';
                                const channelIdForVoice = activeConversation.isYipYap ? 'yipyap' : 'dm';
                                const voiceRoomParticipants = voiceRooms?.[`${activeConversation.id}:${channelIdForVoice}`] || [];
                                const isVoiceRoomActiveHere = activeVoiceData?.communityId === activeConversation.id.toString() && activeVoiceData?.channelId === channelIdForVoice;

                                return (
                                    <div className="w-full flex-shrink-0 z-20 bg-transparent flex flex-col justify-end pb-3 pt-2" style={{ height: 'calc(5.5rem + var(--safe-area-top, 0px))' }}>
                                        <div className="w-full px-4 flex items-center gap-3 bg-transparent">
                                            {isPhone && (
                                                <button onClick={() => onClearConversation?.()} className="p-2 -ml-2 hover:bg-white/10 rounded-full text-white">
                                                    <ChevronLeftIcon className="w-6 h-6" />
                                                </button>
                                            )}
                                            {(() => {
                                                if (activeConversation.isYipYap) {
                                                    const ownerId = activeConversation.yipYapOwner || '';
                                                    const owner = usersData[ownerId];
                                                    const isAnonymous = activeConversation.yipYapMode === 'anonymous';
                                                    const chatName = activeConversation.yipYapName || (isAnonymous ? 'Anonymous Hall' : 'Broadcast Channel');
                                                    return (
                                                        <div className="flex items-center gap-3 flex-grow cursor-pointer" onClick={() => !isAnonymous && onNavigateToProfile(ownerId)}>
                                                            <div className="relative flex-shrink-0">
                                                                <img
                                                                    src={isAnonymous ? 'https://api.dicebear.com/7.x/bottts/svg?seed=anonymous&backgroundColor=b6e3f4' : (owner?.pfp || `https://api.dicebear.com/7.x/bottts/svg?seed=${ownerId}&backgroundColor=b6e3f4`)}
                                                                    className="w-10 h-10 rounded-full object-cover shadow-lg border border-white/10"
                                                                    alt=""
                                                                />
                                                                <span className="absolute -bottom-1 -right-1 text-xs">{isAnonymous ? '👻' : '📢'}</span>
                                                            </div>
                                                            <div className="min-w-0">
                                                                <h3 className="font-bold truncate" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.25) }}>{chatName}</h3>
                                                                <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider">{activeConversation.yipYapMode === 'forum' ? 'Forum Channel' : activeConversation.yipYapMode === 'anonymous' ? 'Anonymous Hall' : 'Broadcast Channel'}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                }


                                                const otherUser = usersData[otherUserId];
                                                if (!otherUser) return null;
                                                const nickname = activeConversation.nicknames?.[otherUserId] || otherUser.displayName;
                                                const isActiveMod = otherUserId === 'goodlyfe' && activeSystemModId;
                                                const activeMod = customMods.find(m => m.id === activeSystemModId);

                                                return (
                                                    <div className="flex items-center gap-3 flex-grow cursor-pointer" onClick={() => onNavigateToProfile(otherUserId)}>
                                                        <div className="relative">
                                                            <img src={otherUser.pfp} className="w-10 h-10 rounded-full object-cover shadow-lg border border-white/10" alt="" />
                                                            {isActiveMod && activeMod && (
                                                                <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-lg overflow-hidden border border-black shadow-lg">
                                                                    <img src={activeMod.icon} alt="" className="w-full h-full object-cover" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="font-bold truncate" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.25) }}>{nickname}</h3>
                                                                {otherUserId === 'goodlyfe' && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onOpenSystemModModal?.();
                                                                        }}
                                                                        className="px-2 py-0.5 rounded-full bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-[9px] font-black text-blue-400 uppercase tracking-widest transition-all"
                                                                    >
                                                                        Mods
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {activeUsers.includes(otherUserId) && (
                                                                <p className="text-[10px] text-green-400 font-bold animate-pulse uppercase tracking-wider" style={stickerTextStyle(0.25)}>Active rn</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            {isVoiceRoomActiveHere ? (
                                                <div id="voice-participants-destination" className="max-w-[120px] sm:max-w-[250px] overflow-hidden" />
                                            ) : voiceRoomParticipants.length > 0 ? (
                                                <div className="flex -space-x-2 mr-2">
                                                    {voiceRoomParticipants.slice(0, 3).map(p => {
                                                        const pfp = usersData[p.username]?.pfp || `https://api.dicebear.com/7.x/bottts/svg?seed=${p.username}&backgroundColor=b6e3f4`;
                                                        return <img key={p.socketId} src={pfp} className="w-8 h-8 rounded-full border-2 border-green-500 object-cover shadow-lg" alt="" />;
                                                    })}
                                                    {voiceRoomParticipants.length > 3 && (
                                                        <div className="w-8 h-8 rounded-full bg-gray-800 border-2 border-green-500 flex items-center justify-center text-[10px] font-bold text-white z-10">
                                                            +{voiceRoomParticipants.length - 3}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : null}

                                            <div className="flex items-center gap-1">
                                                {isPhone && otherUserId === 'goodlyfe' && (activeSystemModId || modPreviewData) && (
                                                    <button
                                                        onClick={() => setShowMobilePreview(!showMobilePreview)}
                                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${showMobilePreview ? 'bg-blue-600 border-blue-500 text-white shadow-lg' : 'bg-white/5 border-white/10 text-gray-400'}`}
                                                    >
                                                        <SparklesIcon className="w-4 h-4" />
                                                        <span className="text-[10px] font-black uppercase tracking-widest">{showMobilePreview ? 'Chat' : 'Preview'}</span>
                                                    </button>
                                                )}
                                                {activeConversation?.isYipYap && activeConversation.visibility === 'open' && (
                                                    <button onClick={() => setShowInviteModal(true)} className="p-2 hover:bg-white/5 rounded-full text-gray-400" title="Invite Followers">
                                                        <UsersIcon className="w-5 h-5" />
                                                    </button>
                                                )}
                                                <button className="p-2 hover:bg-white/5 rounded-full text-gray-400"><SearchIcon className="w-5 h-5" /></button>

                                                {isVoiceRoomActiveHere ? (
                                                    <button onClick={() => onLeaveVoice?.()} className="p-2 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors">
                                                        <CloseIcon className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <button onClick={() => onJoinVoice?.(activeConversation.id.toString(), channelIdForVoice)} className={`p-2 rounded-full transition-colors ${voiceRoomParticipants.length > 0 ? 'bg-green-500 hover:bg-green-600 text-white animate-pulse' : 'hover:bg-white/5 text-gray-400 hover:text-white'}`}>
                                                        <PhoneIcon className="w-5 h-5" />
                                                    </button>
                                                )}

                                                {(!activeConversation.isYipYap || activeConversation.yipYapOwner === currentUserId) && (
                                                    <button onClick={() => setShowThemeModal(true)} className="p-2 hover:bg-white/5 rounded-full text-gray-400 hover:text-white transition-colors"><SettingsIcon className="w-5 h-5" /></button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })()}

                            <div
                                ref={scrollContainerRef}
                                className="flex-grow w-full overflow-y-auto p-4 flex flex-col-reverse gap-1.5 custom-scrollbar relative messages-page-no-swipe"
                                onTouchStart={(e) => {
                                    if (e.touches.length === 2) {
                                        const dist = Math.hypot(
                                            e.touches[0].pageX - e.touches[1].pageX,
                                            e.touches[0].pageY - e.touches[1].pageY
                                        );
                                        initialPinchDistanceRef.current = dist;
                                        initialFontSizeRef.current = messageFontSize;
                                    }
                                }}
                                onTouchMove={(e) => {
                                    if (e.touches.length === 2 && initialPinchDistanceRef.current !== null) {
                                        const currentDist = Math.hypot(
                                            e.touches[0].pageX - e.touches[1].pageX,
                                            e.touches[0].pageY - e.touches[1].pageY
                                        );
                                        const zoomFactor = currentDist / initialPinchDistanceRef.current;
                                        const newSize = Math.max(14, Math.min(48, initialFontSizeRef.current * zoomFactor));
                                        updateMessageFontSize(newSize);
                                    }
                                }}
                                onTouchEnd={() => {
                                    initialPinchDistanceRef.current = null;
                                }}
                                onScroll={(e) => {
                                    const target = e.currentTarget;
                                    const atBottom = Math.abs(target.scrollTop) < 50;
                                    setIsAtBottom(atBottom);

                                    const atTop = Math.abs(target.scrollTop) + target.clientHeight >= target.scrollHeight - 50;
                                    if (atTop && displayLimit < activeConversation.messages.length) {
                                        setDisplayLimit(prev => prev + 20);
                                    }
                                }}>
                                {(() => {
                                    const rawMsgs = (activeConversation?.messages || []).filter(m => !m.hiddenBy?.includes(currentUserId || ''));
                                    const uniqueMap = new Map<string | number, Message>();
                                    rawMsgs.forEach(m => {
                                        const key = m.id || `${m.senderId}_${m.timestamp}_${m.text}`;
                                        uniqueMap.set(key, m);
                                    });
                                    const uniqueMsgs = Array.from(uniqueMap.values());
                                    return uniqueMsgs.slice(-displayLimit).reverse().map((msg, idx, arr) => {
                                    const prevMsg = arr[idx + 1]; // previous in reversed array = older message
                                    const isGrouped = prevMsg && (prevMsg.senderId || (prevMsg as any).authorId) === (msg.senderId || (msg as any).authorId) && (new Date(msg.timestamp).getTime() - new Date(prevMsg.timestamp).getTime() < 300000); // 5 min grouping

                                    return (
                                        <MessageComponent
                                            key={msg.id}
                                            msg={msg}
                                            isOwn={(msg.senderId || (msg as any).authorId) === currentUserId}
                                            otherUser={activeConversation?.participants.filter(p => p !== currentUserId).map(p => usersData[p])[0]}
                                            onReply={setReplyingTo}
                                            onForward={setForwardingMessage}
                                            onDelete={setDeletingMessage}
                                            onAddReaction={(mid, emoji) => onAddReaction(activeConversation!.id, mid, emoji)}
                                            theme={activeConversation?.theme}
                                            hideBubbles={isAnonymous ? true : activeConversation?.hideBubbles}
                                            nicknames={activeConversation?.nicknames}
                                            onNavigateToProfile={onNavigateToProfile}
                                            messageFontSize={messageFontSize}
                                            currentUserId={currentUserId}
                                            messagesById={messagesById}
                                            isGrouped={isGrouped}
                                            onScrollToMessage={scrollToMessage}
                                            onDragStart={(e, mid) => !editingMessageId && handleMessageDragStart(e, mid)}
                                            onDragMove={(e, mid) => !editingMessageId && handleMessageDragMove(e, mid)}
                                            onDragEnd={handleMessageDragEnd}
                                            dragOffset={dragOffsets[msg.id] || 0}
                                            usersData={usersData}
                                            posts={posts}
                                            onDoubleTap={(mid) => {
                                                if (editingMessageId) return;
                                                const now = Date.now();
                                                if (now - (lastReactionTimeRef.current[mid] || 0) < 500) return;
                                                lastReactionTimeRef.current[mid] = now;
                                                const favEmoji = usersData[currentUserId!]?.favoriteEmoji || '❤️';
                                                onAddReaction(activeConversation!.id, mid, favEmoji);
                                            }}
                                            isEditing={editingMessageId === msg.id}
                                            editText={editText}
                                            onEditChange={(val) => setEditText(val)}
                                            onSaveEdit={() => handleSaveEdit(msg.id, editText)}
                                            onCancelEdit={() => { setEditingMessageId(null); setEditText(''); }}
                                            yipYapMode={activeConversation?.yipYapMode}
                                            anonymousAliases={anonymousAliases}
                                            sharedPost={msg.sharedPostId ? (posts.find((p: any) => p.id === msg.sharedPostId) || fetchedSharedPosts[msg.sharedPostId]) : undefined}
                                            onMediaClick={handleMediaClick}
                                            onPostClick={onPostClick}
                                        />
                                    );
                                });
                               })()}

                            </div>

                            {/* Sticky Footer - unified with CommunityDetailPage styling */}
                            {(() => {
                                const channelIdForVoice = activeConversation.isYipYap ? 'yipyap' : 'dm';
                                const isVoiceRoomActiveHere = activeVoiceData?.communityId === activeConversation.id.toString() && activeVoiceData?.channelId === channelIdForVoice;
                                return (
                                    <div className={`w-full flex-shrink-0 bg-transparent p-4 space-y-3 relative z-40 messages-page-no-swipe ${isVoiceRoomActiveHere ? 'pb-4' : 'pb-[calc(1rem+var(--safe-area-bottom,0px))]'}`}>
                                        {replyingTo && (
                                            <div className="flex items-center gap-3 bg-white/5 border border-white/10 p-3 rounded-xl animate-in slide-in-from-bottom-2 duration-200">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <ReplyIcon className="w-3 h-3 text-blue-400" />
                                                        <span className="text-[11px] font-bold text-blue-400 uppercase tracking-wider">Replying to {isAnonymous && anonymousAliases?.[replyingTo.senderId] ? anonymousAliases[replyingTo.senderId].name : (activeConversation?.nicknames?.[replyingTo.senderId] || usersData[replyingTo.senderId]?.displayName || replyingTo.senderId)}</span>
                                                    </div>
                                                    <p className="text-xs text-gray-400 truncate italic">"{replyingTo.text || 'Shared media'}"</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setReplyingTo(null)}
                                                    className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-gray-500 hover:text-white"
                                                >
                                                    <CloseIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}

                                        {attachments.length > 0 && (
                                            <div className="flex flex-wrap gap-2 pb-1">
                                                {attachments.map((at, idx) => (
                                                    <div key={idx} className="relative group flex-shrink-0">
                                                        <div className={`${(at.type === 'audio' || at.type === 'voice') ? 'w-full' : 'w-16'} h-16 rounded-xl overflow-hidden`}>
                                                            {at.type === 'image' && <img src={at.url} className="w-full h-full object-cover" alt="" />}
                                                            {at.type === 'video' && <video src={`${at.url}#t=0.001`} className="w-full h-full object-cover" playsInline muted preload="metadata" />}
                                                            {(at.type === 'audio' || at.type === 'voice') && (
                                                                <div className="w-full h-full flex items-center justify-center p-2">
                                                                    <CustomAudioPlayer src={at.url} />
                                                                </div>
                                                            )}
                                                            {at.type === 'file' && (
                                                                <div className="w-full h-full flex flex-col items-center justify-center p-1 bg-[#1b1b1f] border border-white/10 rounded-xl" title={at.file?.name || 'File'}>
                                                                    <PaperclipIcon className="w-5 h-5 text-gray-400 mb-1 shrink-0" />
                                                                    <span className="text-[8px] text-gray-400 truncate w-full text-center px-1 shrink-0" style={stickerTextStyle(0.1)}>{at.file?.name || 'File'}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeAttachment(idx)}
                                                            className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 shadow-lg flex items-center justify-center hover:bg-red-500 transition-colors"
                                                        >
                                                            <CloseIcon className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="w-full flex flex-col gap-2">
                                            {Object.keys(typingUsers).length > 0 && activeConversation && (
                                                <div className="px-2 flex items-center gap-2 animate-pulse mb-1">
                                                    <div className="flex -space-x-2">
                                                        {Object.keys(typingUsers).map((uid, i) => {
                                                            const isAnon = activeConversation.yipYapMode === 'anonymous';
                                                            const pfp = isAnon && anonymousAliases[uid] ? anonymousAliases[uid].pfp : typingUsers[uid].pfp;
                                                            return <img key={i} src={pfp} alt="" className="w-4 h-4 rounded-full object-cover" />;
                                                        })}
                                                    </div>
                                                    <span className="text-[10px] text-white/60 font-medium italic" style={stickerTextStyle(0.2)}>
                                                        {Object.keys(typingUsers).map(uid => {
                                                            const isAnon = activeConversation.yipYapMode === 'anonymous';
                                                            if (isAnon && anonymousAliases[uid]) return anonymousAliases[uid].name;
                                                            return activeConversation.nicknames?.[uid] || typingUsers[uid].username;
                                                        }).join(', ')} {Object.keys(typingUsers).length === 1 ? 'is yappin...' : 'are yappin...'}
                                                    </span>
                                                </div>
                                            )}

                                            {activeConversation?.isYipYap && activeConversation.yipYapMode === 'broadcast' && activeConversation.yipYapOwner !== currentUserId ? (
                                                <div className="w-full text-center py-4 text-white/40 text-xs font-bold tracking-widest uppercase border border-white/5 bg-white/5 rounded-2xl mb-1 select-none flex flex-col items-center gap-2">
                                                    <span className="text-lg">📢</span>
                                                    Only the creator can send messages in this broadcast channel.
                                                </div>
                                            ) : (
                                                <form onSubmit={handleSendMessage} className="w-full flex gap-2 items-end">
                                                    <div className="w-full flex flex-col gap-2">
                                                        {showMessageRecorder && (
                                                            <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl px-4 py-2 border border-blue-500/30 min-h-[48px] flex items-center mb-1">
                                                                <AudioRecorder
                                                                    ref={audioRecorderRef}
                                                                    onRecordingComplete={handleMessageRecorderComplete}
                                                                    onReset={handleMessageRecorderReset}
                                                                    autoStart={true}
                                                                    showControls={false}
                                                                    stream={audioStream}
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="flex-1 relative bg-black/40 rounded-full focus-within:border-blue-500/50 transition-all duration-200 flex items-center px-2">
                                                            <div className="flex-none relative">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setShowInputMenu(!showInputMenu)}
                                                                    className={`p-2 rounded-xl transition-all ${showInputMenu ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                                    title="More options"
                                                                >
                                                                    <PlusIcon className={`w-5 h-5 transition-transform duration-200 ${showInputMenu ? 'rotate-45' : ''}`} />
                                                                </button>
                                                                {showInputMenu && (
                                                                    <>
                                                                        <div className="fixed inset-0 z-40" onClick={() => setShowInputMenu(false)} />
                                                                        <div className="absolute bottom-full left-0 mb-2 bg-[#1b1b1f] border border-white/10 rounded-full shadow-2xl overflow-hidden flex flex-row items-center p-1 animate-fade-in z-50">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setShowInputMenu(false);
                                                                                    const isAndroid = Capacitor.getPlatform() === 'android' || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));
                                                                                    if (isAndroid) {
                                                                                        setCameraMode('image'); 
                                                                                        setShowCamera(true);
                                                                                    } else {
                                                                                        setCameraMode('both'); 
                                                                                        setShowCamera(true);
                                                                                    }
                                                                                }}
                                                                                className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                            >
                                                                                <CameraIcon className="w-5 h-5 text-blue-400" />
                                                                            </button>
                                                                            {(() => {
                                                                                const isAndroid = Capacitor.getPlatform() === 'android' || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));
                                                                                if (isAndroid) {
                                                                                    return (
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => {
                                                                                                setShowInputMenu(false);
                                                                                                setCameraMode('video'); 
                                                                                                setShowCamera(true);
                                                                                            }}
                                                                                            className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                                        >
                                                                                            <VideoIcon className="w-5 h-5 text-purple-400" />
                                                                                        </button>
                                                                                    );
                                                                                }
                                                                                return null;
                                                                            })()}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setShowInputMenu(false);
                                                                                    handleGalleryClick();
                                                                                }}
                                                                                className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                            >
                                                                                <PaperclipIcon className="w-5 h-5 text-green-400" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setShowInputMenu(false);
                                                                                    setShowTenorModal(true);
                                                                                }}
                                                                                className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                            >
                                                                                <span className="text-[10px] font-black tracking-widest px-1.5 py-1 rounded bg-pink-500/20 text-pink-400 border border-pink-500/30 leading-none flex items-center justify-center h-5">GIF</span>
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={async () => {
                                                                                    setShowInputMenu(false);
                                                                                    if (!showMessageRecorder) {
                                                                                        try {
                                                                                            await requestMicrophonePermission();
                                                                                            const stream = await navigator.mediaDevices.getUserMedia({
                                                                                                audio: VOICE_AUDIO_CONSTRAINTS
                                                                                            });
                                                                                            setAudioStream(stream);
                                                                                            setShowMessageRecorder(true);
                                                                                        } catch (err) {
                                                                                            console.error("Microphone access failed:", err);
                                                                                        }
                                                                                    } else {
                                                                                        audioRecorderRef.current?.stopRecording();
                                                                                        setShowMessageRecorder(false);
                                                                                    }
                                                                                }}
                                                                                className={`p-2.5 hover:bg-white/5 rounded-full transition-colors ${showMessageRecorder ? 'text-red-400' : 'text-white'}`}
                                                                            >
                                                                                <MicIcon className={`w-5 h-5 ${showMessageRecorder ? 'text-red-400 animate-pulse' : 'text-red-400'}`} />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setShowInputMenu(false);
                                                                                    setShowEmojiPickerFor('input');
                                                                                }}
                                                                                className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                                                title="Emoji"
                                                                            >
                                                                                <EmojiIcon className="w-5 h-5 text-yellow-400" />
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                )}
                                                                <input type="file" id="msg-file" multiple hidden onChange={handleMediaUpload} accept="*/*" />
                                                            </div>

                                                            <textarea
                                                                ref={textareaRef}
                                                                className="flex-1 bg-transparent text-white px-3 py-3 focus:outline-none resize-none min-h-[48px] max-h-[150px] transition-all duration-200"
                                                                rows={1}
                                                                value={messageInput}
                                                                onChange={handleInputChange}
                                                                onKeyDown={handleMessageKeyDown}
                                                                onPaste={handlePaste}
                                                                placeholder={activeConversation?.participants.includes('goodlyfe') && activeSystemModId ? "Describe the UI you want to build..." : "Go ahead and start yappin..."}
                                                                disabled={isUploadingMedia}
                                                            />
                                                            {mentionQuery !== null && (
                                                                <MentionDropdown
                                                                    query={mentionQuery}
                                                                    following={usersData[currentUserId || '']?.following || []}
                                                                    usersData={usersData}
                                                                    onSelect={(username) => {
                                                                        const { newText, newCursorPos } = completeMention(messageInput, username, messageInput.length);
                                                                        setMessageInput(newText);
                                                                        updateSearch(newText, newCursorPos); // Clear/update search
                                                                        setTimeout(() => {
                                                                            if (textareaRef.current) {
                                                                                textareaRef.current.focus();
                                                                                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                                                                            }
                                                                        }, 10);
                                                                    }}
                                                                    position="above"
                                                                />
                                                            )}

                                                            <button
                                                                type="submit"
                                                                disabled={(!messageInput.trim() && attachments.length === 0) || isUploadingMedia}
                                                                className="p-2 hover:bg-white/5 rounded-xl text-blue-500 disabled:opacity-50 transition-all flex-shrink-0 ml-1"
                                                            >
                                                                {isUploadingMedia ? <span className="animate-spin inline-block w-5 h-5 border-2 border-t-transparent border-current rounded-full" /> : <SendIcon className="w-5 h-5" />}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            )}

                                            {isVoiceRoomActiveHere && (
                                                <div id="voice-controls-destination" className="w-full mt-4 flex items-center justify-center border-t border-white/10 pt-4" />
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}
                        </>
                    ) : (
                        <div className="flex-grow flex flex-col items-center justify-center p-8 text-center text-raised">
                            <div className="w-24 h-24 rounded-[2rem] bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mb-6 shadow-2xl border border-white/5 animate-bounce-slow">
                                <MailIcon className="w-10 h-10 text-blue-500/80" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2" style={stickerTextStyle(0.3)}>Yappin Central</h2>
                            <p className="text-white/40 max-w-xs text-sm leading-relaxed" style={stickerTextStyle(0.2)}>Select a conversation or start a new one to begin your digital footprint journey.</p>
                        </div>
                    )}
                </div>

                {/* Mod Preview Panel */}
                {(() => {
                    const isSystemChat = activeConversation?.participants.includes('goodlyfe');
                    const isModEditingMode = isSystemChat && activeSystemModId;
                    if (!isModEditingMode || (isPhone && !showMobilePreview)) return null;
                    const activeMod = customMods.find(m => m.id === activeSystemModId);

                    return (
                        <div className={`${isPhone ? 'w-full' : 'w-[400px] border-l'} border-white/10 bg-[#0a0a0b] flex flex-col animate-slide-in-right`}>
                            <div className="w-full flex-shrink-0 z-20 bg-black/20 border-b border-white/5 flex flex-col justify-end pb-3 pt-2 px-4" style={{ height: 'calc(5.5rem + var(--safe-area-top, 0px))' }}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {isPhone && (
                                            <button 
                                                onClick={() => setShowMobilePreview(false)}
                                                className="p-1 hover:bg-white/5 rounded-lg transition-colors mr-1 flex items-center justify-center"
                                                aria-label="Back to chat"
                                            >
                                                <ChevronLeftIcon className="w-5 h-5 text-gray-400" />
                                            </button>
                                        )}
                                        <div className="w-8 h-8 rounded-full overflow-hidden border border-white/10 flex-shrink-0">
                                            <img src={activeMod?.icon} alt="" className="w-full h-full object-cover" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-black text-white italic uppercase leading-none mb-0.5">{activeMod?.name}</h3>
                                            <p className="text-[9px] text-blue-400 font-bold uppercase tracking-widest leading-none">Live Preview</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                        <span className="text-[8px] font-black text-green-500/70 uppercase">Connected</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex-grow flex items-center justify-center bg-[#111] overflow-hidden relative">
                                {(() => {
                                    const modDef = MOD_REGISTRY[activeSystemModId!];
                                    if (!modDef) {
                                        // If the mod has a source URL, render it via the proxy to bypass X-Frame-Options
                                        if (activeMod?.url) {
                                            return (
                                                <ProxiedIframe
                                                    url={activeMod.url}
                                                    className="w-full h-full border-none"
                                                    title={activeMod.name}
                                                />
                                            );
                                        }
                                        return modPreviewData ? (

                                            <div className="w-full h-full p-8 overflow-y-auto custom-scrollbar">
                                                <div className="bg-white/5 rounded-2xl p-6 border border-white/5">
                                                    <h4 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-4">Generated Interface</h4>
                                                    <div className="text-white/80 text-sm whitespace-pre-wrap font-mono text-[10px]">
                                                        {modPreviewData}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-full h-full rounded-[2rem] border border-white/5 bg-black/40 flex flex-col items-center justify-center p-6 text-center shadow-2xl overflow-hidden group">
                                                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                <div className="w-20 h-20 rounded-[1.5rem] bg-white/5 flex items-center justify-center mb-4 border border-white/5 group-hover:scale-110 transition-transform">
                                                    <SparklesIcon className="w-10 h-10 text-blue-500/40" />
                                                </div>
                                                <h4 className="text-white/60 font-bold text-sm mb-2 italic">Waiting for your first prompt...</h4>
                                                <p className="text-[10px] text-white/30 uppercase tracking-widest font-medium max-w-[200px]">GoodieBoy is ready to generate your modular interface.</p>
                                            </div>
                                        );
                                    }

                                    const mod = getDynamicModDefinition(modDef, usersData);
                                    const ModComponent = mod.component;

                                    const previewLog = (msg: string) => console.log(`[Preview Mode] ${msg}`);

                                    return (
                                        <div className="w-full h-full overflow-y-auto custom-scrollbar relative">
                                            <React.Suspense fallback={
                                                <div className="flex items-center justify-center h-full">
                                                    <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
                                                </div>
                                            }>
                                                <ModComponent
                                                    params={{}}
                                                    currentUser={currentUserId}
                                                    usersData={usersData}
                                                    appThemeColor={appThemeColor}
                                                    onNavigate={() => previewLog('Navigation disabled')}
                                                    setPage={() => previewLog('Navigation disabled')}
                                                    updateUserData={() => previewLog('Update user data disabled')}
                                                    currentOpacity={postBlurOpacity}
                                                    onNavigateToProfile={() => previewLog('Profile navigation disabled')}
                                                    onPostClick={onPostClick}
                                                    // Compatibility aliases
                                                    username={currentUserId}
                                                    themeColor={appThemeColor}
                                                    currentUserId={currentUserId}
                                                    currentUserData={usersData[currentUserId]}
                                                    // Core Page Props
                                                    posts={posts}
                                                    allUsers={usersData}
                                                    allPosts={posts}
                                                    animatingLikes={animatingLikes}
                                                    animatingBumps={animatingBumps}
                                                    animatingBookmarks={animatingBookmarks}
                                                    galleryAnimation={galleryAnimation}
                                                    postBlurOpacity={postBlurOpacity}
                                                    gridColumns={gridColumns}
                                                    onGridColumnsChange={onGridColumnsChange}
                                                    onLike={() => previewLog('Like disabled')}
                                                    onBump={() => previewLog('Bump disabled')}
                                                    onBookmark={() => previewLog('Bookmark disabled')}
                                                    onEdit={() => previewLog('Edit disabled')}
                                                    onDelete={() => previewLog('Delete disabled')}
                                                    onVouch={() => previewLog('Vouch disabled')}
                                                    onReportPost={() => previewLog('Report disabled')}
                                                    onNavigateToHashtag={() => previewLog('Hashtag navigation disabled')}
                                                    onShare={() => previewLog('Share disabled')}
                                                    onMuteUser={() => previewLog('Mute disabled')}
                                                    onBlockUser={() => previewLog('Block disabled')}
                                                    onGrantAdmin={() => previewLog('Admin grant disabled')}
                                                    onDeleteUser={() => previewLog('User delete disabled')}
                                                    dailyAchievements={dailyAchievements}
                                                    onCompleteAchievement={() => previewLog('Achievement disabled')}
                                                    onOpenAdminSettings={() => previewLog('Admin settings disabled')}
                                                    isAdmin={isAdmin}
                                                    communities={communities}
                                                    socket={null} // No socket in preview
                                                    voiceRooms={voiceRooms}
                                                    isDemo={true} // Force demo mode
                                                    onJoinVoice={() => previewLog('Voice disabled')}
                                                    onLeaveVoice={() => previewLog('Voice disabled')}
                                                    activeVoiceData={activeVoiceData}
                                                />
                                            </React.Suspense>
                                            <VisualModPlayground
                                                elements={playgroundElements}
                                                onAddElement={(type, variant, props) => {
                                                    const id = Math.random().toString(36).substr(2, 9);
                                                    setPlaygroundElements([...playgroundElements, { id, type, variant, props, x: 50, y: 50 }]);
                                                }}
                                                onUpdateElement={(id, updates) => {
                                                    setPlaygroundElements(playgroundElements.map(el => el.id === id ? { ...el, ...updates } : el));
                                                }}
                                                onRemoveElement={(id) => {
                                                    setPlaygroundElements(playgroundElements.filter(el => el.id !== id));
                                                }}
                                                isPhone={isPhone}
                                            />
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="p-4 bg-black/40 border-t border-white/5 pb-[calc(1rem+var(--safe-area-bottom,0px))]">
                                <button
                                    onClick={() => onPublishMod?.(activeSystemModId!, modPreviewData)}
                                    className="w-full py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 disabled:opacity-50"
                                    disabled={!modPreviewData}
                                >
                                    Publish to Pimp My Ride
                                </button>
                            </div>
                        </div>
                    );
                })()}
            </div>
            {showInviteModal && activeConversation && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={() => setShowInviteModal(false)} />
                    <div className="relative w-full max-w-sm rounded-3xl p-6 bg-gray-900 border border-white/10 animate-scale-up">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-white">Invite Followers</h2>
                            <button type="button" onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-white transition-colors p-1">
                                <CloseIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <input
                            type="text"
                            placeholder="Search followers..."
                            className="w-full bg-black/40 border border-white/5 rounded-2xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-500/50 mb-4 transition-colors"
                            value={inviteSearchTerm}
                            onChange={(e) => setInviteSearchTerm(e.target.value)}
                        />
                        <div className="max-h-60 overflow-y-auto no-scrollbar space-y-2 pr-1">
                            {usersData[currentUserId]?.followers?.length ? (
                                usersData[currentUserId].followers
                                    .filter(f => !activeConversation.participants.includes(f))
                                    .filter(f => {
                                        if (!inviteSearchTerm) return true;
                                        const tU = usersData[f];
                                        return (tU?.displayName || f).toLowerCase().includes(inviteSearchTerm.toLowerCase());
                                    })
                                    .map(followerId => {
                                        const follower = usersData[followerId];
                                        return (
                                            <div key={followerId} className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <img src={follower?.pfp || `https://api.dicebear.com/7.x/bottts/svg?seed=${followerId}&backgroundColor=b6e3f4`} className="w-8 h-8 rounded-full object-cover shadow-sm border border-white/10" alt="" />
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-bold text-white leading-tight">{follower?.displayName || followerId}</span>
                                                        <span className="text-[10px] text-gray-500">@{followerId}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleInviteFollower(followerId)}
                                                    className="px-4 py-1.5 rounded-full bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 text-[10px] font-black uppercase tracking-widest border border-blue-500/30 transition-all active:scale-95"
                                                >
                                                    Invite
                                                </button>
                                            </div>
                                        );
                                    })
                            ) : (
                                <div className="text-center py-6">
                                    <p className="text-sm font-bold text-white">No followers to invite</p>
                                    <p className="text-[10px] text-gray-500 mt-1">
                                        {inviteSearchTerm ? "No followers match your search." : "All your followers are already members."}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <NativeCamera
                isOpen={showCamera}
                onClose={() => setShowCamera(false)}
                onCapture={handleCameraCapture}
                onOpenGallery={() => {
                    setShowCamera(false);
                    document.getElementById('msg-file')?.click();
                }}
                captureMode={cameraMode}
            />
            <MediaViewerModal
                isOpen={activeMediaViewerIndex !== null}
                onClose={() => setActiveMediaViewerIndex(null)}
                mediaList={conversationMedia}
                initialIndex={activeMediaViewerIndex ?? 0}
                anonymousAliases={isAnonymous ? anonymousAliases : undefined}
            />

            {/* GIF Search Modal — Tenor, Giphy & Klipy */}
            <GifPickerModal
                isOpen={showTenorModal}
                onClose={() => setShowTenorModal(false)}
                onSelectGif={(url) => handleAttachTenorGif(url)}
                title="Search GIFs"
                zIndexClass="z-[150]"
            />
        </div>
    );
};
